"use client";

/* ───────────────────────────────────────────────────────────────────────────
 * Emulsion
 *
 * A photographer's WALL. Scrolling doesn't move a page — it dollies a camera
 * sideways along a long bone-coloured wall where photographs hang from pins.
 * SEVEN beats, TWO giant vermillion words, NINE photographs.
 *
 * THE SHAPE
 *   0  `photographer` fills the frame; three prints right of centre
 *   1  the word climbs to the middle and runs ALL the way RIGHT, until its own
 *      origin is on the frame edge and you read it from the start; the three
 *      prints walk LEFT, each at its own pace, and come to rest CENTRED —
 *      then the trio PINS — and stays pinned for the whole life of the word,
 *      so `photographer` travels right, holds, and slides out of frame from
 *      BEHIND the still images. Right, still, left.
 *   2  handoff — `photographer` leaves as `work` arrives, three prints sliding
 *      in behind them so the beat is never a bare wall
 *   3  `work` PINS. The left group lands and freezes with it
 *   4  three more TRAVEL IN from off-frame right and freeze alongside
 *   5  the pin releases; the whole formation slides off as one
 *   6  the contact board
 *
 * ONE animation, on PAGE LOAD: the photographs fade up 0 → 1 over ~0.95s, and
 * the inline opacity is then cleared for good. That fade starts when the film
 * has DECODED, not on a timer — a print must never change after you can see
 * it. NOTHING ANIMATES ON SCROLL-IN — not the photographs, not the contact
 * board.
 * No fade, no develop, no swing-on-arrival, no visibility gate: a photograph is
 * simply on the wall, fully formed, and you
 * travel past it. Prints enter by TRAVELLING in from off-frame right, which the
 * layout guarantees on its own: each print's leading edge crosses x=1.0 exactly
 * on its own beat, so geometry does the whole job an arrival gate used to.
 *
 * Three things carry it:
 *
 *   1 · EVERY FRAME IS A PENDULUM, and only YOU disturb it. Each photograph
 *       hangs from a pin somewhere along its top edge — never the centre — so
 *       it hangs crooked, the way a real print does, and swings on
 *       θ'' = −(g/L)·sin(θ−θ₀) − c·θ'. Scrolling does not touch it: the wall
 *       pans and the prints ride it dead still. Sweep the cursor through a
 *       cluster and they shove away from it; GRAB one and you swing it by hand
 *       — the angle tracks your pointer around the pin, and letting go hands
 *       the release velocity straight back to the pendulum, so each rings down
 *       at its own rate because each carries its own arm length.
 *
 *   2 · THE PIN. `work` and the prints around it hold one screen position for
 *       a whole stretch of wall. There is no sticky code path: a print already
 *       carries two anchors, and with a LINEAR blend they cancel the parallax
 *       exactly when they are equal, so `n.x === x` IS the pin. Anchoring the
 *       right-hand group one beat LATER than the wall it joins is what lets it
 *       travel in first and only then freeze.
 *
 *   3 · THE HEADLINE IS RIGID, and that is a decision. It is drawn glyph-by-
 *       glyph on canvas, so per-letter springs and baseline ripples are free to
 *       add — and both were, and both had to go. At half the viewport height a
 *       wave is not a texture, it is a wobble. The glyph loop stays because it
 *       is what makes per-glyph POSITIONING possible; it just never animates.
 *
 * The camera is direct — one short smoothing lerp, no magnet and no momentum,
 * so the wall stops exactly where the wheel stops. Depth is real: prints are
 * anchored to their own stop and parallax away from it, so every composition
 * lands exact while travel still separates the planes, and the far ones carry a
 * sub-pixel blur. Film grain jitters over the whole scene — a still grain is
 * wallpaper, this one is running film.
 *
 * One raw-rAF clock owns the camera, the pendulums and the canvas paint. GSAP
 * owns only the copy + chrome reveal; the photographs have no load animation
 * either — the wall is already hung when the page opens. Theme-aware (bone wall
 * / dark studio), pauses off-screen and in hidden tabs, honours reduced motion.
 * ─────────────────────────────────────────────────────────────────────────── */

import type { FormEvent } from "react";

import { useEffect, useRef, useState } from "react";
import { gsap } from "@/lib/animations/gsap-setup";

/* ─────────────────────────── helpers ─────────────────────────── */

const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);
const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
/** smootherstep — the develop curve and every reveal ride this */
const smooth = (x: number) => {
  const c = clamp01(x);
  return c * c * c * (c * (c * 6 - 15) + 10);
};

/* ─────────────────────────── palette ─────────────────────────── */

interface Pal {
  wall: string; // the wall itself
  ink: string; // primary type
  sub: string; // secondary type
  nav: string; // chrome ink — sits against its own halo
  hair: string; // hairlines
  accent: string; // the vermillion
  card: string; // print stock
  shadowNear: string;
  shadowFar: string;
  grain: number; // grain alpha
  vignette: string;
  panelInk: string; // type on the vermillion card
}

const LIGHT: Pal = {
  wall: "#EFE7DA",
  ink: "#1C1815",
  sub: "#6B6156",
  nav: "rgba(28,24,21,0.70)",
  hair: "rgba(28,24,21,0.22)",
  accent: "#CE4429",
  card: "#FCF8F1",
  shadowNear: "0 26px 48px -18px rgba(50,34,22,0.42), 0 3px 10px rgba(50,34,22,0.16)",
  shadowFar: "0 14px 26px -12px rgba(50,34,22,0.30), 0 2px 6px rgba(50,34,22,0.12)",
  grain: 0.085,
  vignette:
    "radial-gradient(125% 105% at 50% 42%, rgba(0,0,0,0) 52%, rgba(74,52,32,0.10) 82%, rgba(58,38,22,0.20) 100%)",
  panelInk: "#FDF6EC",
};

const DARK: Pal = {
  wall: "#15120F",
  ink: "#EFE6D7",
  sub: "#9A8F80",
  nav: "rgba(239,230,215,0.72)",
  hair: "rgba(239,230,215,0.22)",
  accent: "#E2573A",
  card: "#F3ECE0",
  shadowNear: "0 30px 56px -18px rgba(0,0,0,0.72), 0 4px 12px rgba(0,0,0,0.44)",
  shadowFar: "0 16px 30px -12px rgba(0,0,0,0.58), 0 2px 8px rgba(0,0,0,0.34)",
  grain: 0.1,
  vignette:
    "radial-gradient(125% 105% at 50% 42%, rgba(0,0,0,0) 46%, rgba(0,0,0,0.30) 80%, rgba(0,0,0,0.52) 100%)",
  panelInk: "#FFF3E8",
};

function readTheme(): "light" | "dark" {
  if (typeof document === "undefined") return "light";
  return document.documentElement.getAttribute("data-theme") === "dark"
    ? "dark"
    : "light";
}

/* ─────────────────────────── the wall ─────────────────────────── */

const IMG = "/assets/sections/emulsion/";

/** Camera stops, in multiples of the stage width. The last one is the contact
 *  card, parked further out so the frames have room to clear the frame first. */
/* Beats are 1.15·stage apart, and that number falls out of the print rate.
 * A print previews into the PREVIOUS beat unless it is pushed off the right
 * edge by the travel between them: it needs `x + gap·rate > 1 + halfWidth`.
 * At rate 0.78 and a leftmost print at x≈0.19 with hw≈0.07, that forces
 * gap > 1.13. Tighten the beats and every chapter shows its neighbour's
 * prints early; the slow-print reading is what pays for the wider stride. */
/* SIX beats. Beat 2 is a HANDOFF — no copy of its own; the first word walks
 * out to the left while the second walks in from the right and a new cluster of
 * prints is hung. It's the breath between the two halves of the section. */
/* Beats are NOT evenly spaced, and the long one is deliberate: 2 → 3 is the
 * word handoff, and its length is not a taste decision (see below). Even
 * spacing reads as a metronome and rushes the beat that asks you to read. */
/* SEVEN stops. Beats 3→5 are the PIN: `work` and every photograph from that
 * point hold a fixed screen position while the wall stays still and two groups
 * of prints are hung into it — left at beat 3, right at beat 4. Beat 5 releases
 * and the whole formation slides off together.
 *
 * A PIN IS ONLY AS LONG AS WHAT ARRIVES INSIDE IT. The last print lands at
 * 3.10; hold the release at 5.0 and the wall sits frozen for 1.9 with nothing
 * left to happen in it. What sizes the tail is the READING, not the pin: the
 * statement is in by 3.05, and 0.65 of camera to read it is what the about
 * paragraph gets for a text the same length. Hence release at 3.7. */
/* Beat 1 opens a LONG PIN: the opening trio holds from stop 1 until the tail
 * — long enough for `photographer` to travel right, hold, and start sliding out
 * from BEHIND the still images, which is the whole point of holding them. The
 * word itself only holds to stop 2; the about-paragraph holds with the TRIO
 * (not with the word) so the left column is not left bare while the word leaves.
 *
 * A PIN COSTS EVERY DOWNSTREAM BEAT. Holding `photographer` means its clock
 * lags for the rest of the wall, so it is still on screen that much later than
 * it used to be — and `work`, arriving on the old schedule, walked straight
 * through it (measured 422px of overlap).
 *
 * WHAT ACTUALLY SETS THE LENGTH OF THE HANDOFF. Both words travel at the same
 * rate, so their separation is CONSTANT in `cam` — and it depends on the GAP
 * BETWEEN THE TWO STOPS, not on where either one sits:
 *
 *     separation = 0.21·S + rate·(S3 − S2)·S − Wp        Wp = word advance
 *
 * so a positive gap needs `rate·(S3 − S2) > Wp/S − 0.21`. `photographer` is
 * ~1.25 stage wide on a 16:10 window and ~1.36 on 16:9, which at rate 0.8
 * forces `S3 − S2 ≳ 1.44`. It runs 1.60, which buys ~360px of clear wall
 * between the two words at 16:10 and ~190px at 16:9.
 *
 * THIS IS THE ONE INTERVAL THAT CANNOT BE SHORTENED without either speeding
 * the type up — which would flatten the type-slow/prints-fast parallax the
 * whole section is built on — or letting the two giant words touch. Every
 * other interval was cut to trim the scroll; `S3 − S2` was held at 1.60 and
 * both stops moved together. Shorten the wall again and take it from the
 * beats around this one, never from this one.
 *
 * STOP 1 IS ALSO FIXED, for a different reason. A copy block's exit fade runs
 * `from 0.15 → 0.25` of its own travel, so the intro is not gone until `0.25`;
 * the about paragraph's window opens at `S1 − lead` = `S1 − 0.09`. Cutting S1
 * to 0.25 opens that window at 0.16 and the two paragraphs overlap — measured
 * at cam 0.209, intro still at 0.397 while about was already at 0.123. `S1 =
 * 0.35` is the smallest value that keeps ONE paragraph on screen.
 *
 * 0.35 IS A FLOOR, NOT A TARGET, and it was being used as one. The opening is
 * the most loaded beat in the section — the headline lifts off the floor, walks
 * right AND shrinks, the trio re-hangs to the centre AND shrinks, the intro
 * leaves and the about paragraph rises — and it was running in LESS camera than
 * either reading beat (0.70) and under a quarter of the handoff (1.60). At
 * `SCROLL 2.6` that is ~194px of wheel on a 1440 stage: one trackpad flick puts
 * the whole composition through its entire change of state, which is why it
 * read as a jump-cut rather than a move. It now runs 0.80 — a shade over a
 * reading beat, which is what the amount of simultaneous motion in it earns.
 *
 * EVERY OTHER INTERVAL IS UNCHANGED. The delta is added to stops 1..6 together,
 * so `S2 − S1` is still 0.35, `S3 − S2` is still the untouchable 1.60, and the
 * three tail beats still run 0.70 / 0.70 / 1.00. Widening the opening cost the
 * wall 0.45 of total length and nothing else.
 *
 * WHAT DOES NOT SCALE WITH IT: the intro's exit is set by its own travel, not
 * by the beat — at rate 1.4 it clears the left edge at cam 0.246 whatever S1
 * is. Stretching the beat without touching anything else therefore left the
 * left column BARE from 0.25 to `S1 − 0.09`.
 *
 * THE FIRST FIX FOR THAT WAS WRONG AND IS WORTH RECORDING. Pulling the about
 * paragraph's cue forward (`lead 0.56`) closed the gap, but it put the TEXT IN
 * BEFORE THE PRINTS LANDED — text home at 0.40, trio still migrating until
 * 0.80. The section's whole grammar is one thing at a time, and that inverted
 * the order the opening is built on. The paragraph is not allowed to lead the
 * photographs.
 *
 * SO THE OPENING IS THREE MOVEMENTS, NOT TWO, and `S2 − S1` pays for the third:
 *
 *     0.00 → 0.60   the intro holds, then leaves
 *     0.80          the trio settles into the centre; the word is still parked
 *     0.95 → 1.15   only now does `01 — ABOUT` rise
 *     1.40          only THEN does `photographer` start travelling left
 *
 * `S2 − S1` runs 0.50, not the 0.35 it was trimmed to, because the paragraph
 * now needs a beat of its own between the prints arriving and the word
 * leaving. That interval was only ever a trim; `S3 − S2` is the one that
 * cannot move, and it is still 1.60. The intro gets there by HOLDING to 0.40
 * and then leaving at its own rate (see `data-holdat`) rather than by being
 * slowed — dropping its rate to cover the same ground would have pushed the
 * copy layer slower than the giant type behind it and reversed the depth.
 *
 * S2 IS SET BY WHEN THE PARAGRAPH IS FINISHED, not by a round number. Its last
 * line lands at 1.15, and the word is frozen until S2, so every stage unit
 * between the two is scroll that moves nothing but the trio's slow drift. At
 * 1.40 that gap was 0.25 and read as a stall; 1.30 leaves 0.15 — long enough
 * to register the composition as settled, short enough that the next scroll
 * after the words arrive is the one that starts the wall moving again. Move
 * the paragraph's timing and this has to move with it. */
const STOPS = [0, 0.8, 1.3, 2.9, 3.6, 4.3, 5.3];

/* THE `work` FORMATION FREEZES. THE OPENING TRIO STILL DRIFTS.
 *
 * It used to be that neither froze. The argument for the creep is recorded
 * below and it is a real one, but it lost on the beat that matters most: a
 * word announced as PINNED while nine photographs are hung around it has to be
 * the fixed thing they are hung against. Creeping, `work` travelled 0.14 stage
 * — 200px — over the pin, so the one element the eye uses as the anchor was
 * the element quietly moving. Held means held. The formation and the word now
 * share one screen position from beat 3 until the release, and the release is
 * the first frame anything in it moves.
 *
 * THE COST, KEPT HONEST: between the last print landing (S3 + 1.02) and the
 * release (S3 + 1.40) there is now ~0.38 stage where the wheel turns and the
 * formation does not answer. The statement paragraph is what fills it. If that
 * ever reads as stalled, take it out of `S5 − S4`, not out of the freeze.
 *
 * THE TABLE'S `x` VALUES ARE THE DESIGN POSITIONS AGAIN. They used to be
 * pre-compensated for the drift accumulated before each print arrived —
 *
 *     x   = wallX − HOLD_DRIFT · (st − S3)          where it lands
 *     n.x = wallX − HOLD_DRIFT · (RELEASE − S3)     where it is at the release
 *
 * — which is why they looked arbitrary. Adding the compensation back recovers
 * `wallX` for all nine, and it comes out as an even 0.115 run from 0.05 to
 * 0.97, which is the check that the derivation was right. Both anchors now sit
 * on `wallX`, so `x === n.x` for every pinned print and for `work`: the two
 * anchors cancel the parallax exactly and the body cannot shear, because
 * nothing in it is moving relative to anything else.
 *
 * HOLD_DRIFT ITSELF STAYS — the opening trio and `photographer` still creep on
 * it through their own hold (`HOLD_CLOCK_*`), where the beat is short enough
 * that the wheel never goes unanswered for long. */
const HOLD_DRIFT = 0.1; // stage per stage — the OPENING hold only


/* Where the opening pin lets go, in stage units — deliberately NOT a stop.
 *
 * A pin has to end when the composition behind it is spent. `photographer`
 * starts moving at stop 2 and is ~1.25 stage wide, so ~0.55 later its tail has
 * cleared the right edge and you are watching the word LEAVE. Hold past that
 * and the trio is three photographs frozen in the middle of empty wall while
 * the next word arrives around them — a full stage of nothing when it was
 * sized to the next stop instead. Release here and the prints ride out on the
 * same wall that is carrying the word away, which is what a pan looks like.
 *
 * IT TRACKS STOP 2, so it moves whenever the beats do: `S2 + 0.55`. Leave it
 * behind when the wall is shortened and the pin outlives the word again.
 *
 * The About paragraph releases on the same value. It was pinned so it could be
 * read UNDER the still trio; a paragraph left frozen at the bottom-left of a
 * moving wall reads as a stuck element, not a held one. They are one
 * composition — they let go together. */
/* The trio's schedule is expressed as offsets from TAIL_AT, not as absolute
 * camera positions — `tailPush` and `tailExit` in the table — so that when the
 * contact moves with the viewport the whole beat moves with it. Desktop works
 * out at push 1.35 / 1.50 / 1.65 and exit 1.80 / 1.83 / 1.86; narrow at
 * 0.70 / 0.85 / 1.00 and 1.15 / 1.18 / 1.21. */

/* THE END OF THE WORD SHOVES THE TRIO INTO A PILE.
 *
 * The trio is frozen only while the word is — both are parked between stops 1
 * and 2 — and from the frame `photographer` starts travelling left the prints
 * are picked up one at a time, RIGHT TO LEFT, as if its trailing edge were
 * ploughing them along. Each one, once caught, travels at exactly the word's
 * rate, so it holds a fixed offset from the type: carried, not merely moving.
 *
 * The stagger is the whole effect. A print catches `PUSH_STAGGER` of camera
 * after the one to its right, so for that stretch it is still while its
 * neighbour is already moving — and the gap between them closes by
 * `PUSH_STAGGER × 0.8 = 0.12 stage` (181px at 1512). The two resting gaps are
 * 243px and 269px, so they shut to 62px and 88px: the three photographs end up
 * very nearly on top of one another, at three different heights, and then all
 * release one at a time and leave.
 *
 * NOTE THE ORDER IS NOT DECORATIVE. Right-to-left is what reads as a push;
 * left-to-right would read as the leftmost fleeing and would OPEN the gaps
 * instead of closing them, and simultaneous is the dead freeze this replaces.
 * The stagger must also stay under the resting gap ÷ 0.8 ÷ stage (0.20 here)
 * or a print overtakes its neighbour and the run changes order. */
/* THE TRIO RIDES THE WORD TO ITS END BEFORE IT LEAVES.
 *
 * It creeps at HOLD_DRIFT while the word travels at 0.8, so relative to the
 * type it migrates steadily RIGHTWARD — and `TAIL_AT` is where that migration
 * finishes: solved, not guessed, as the camera position at which the leading
 * print's right edge meets the word's own right edge. At 1512×872 the word
 * spans −857..1037 there and the print's edge is at 1037. Only then does the
 * tail catch it and shove it along.
 *
 * It is a tuned constant because the word's width in stage units
 * swings from 1.22 on 16:9 to 2.48 on a phone; there is no single cam that is
 * exactly the tail everywhere. Re-solve it if the word's size or rate moves.
 *
 * PUSH_STAGGER HAS A HARD CEILING. While one print is being carried at 0.8 and
 * the next is still creeping at 0.1, the gap between them closes at 0.7 per
 * stage — so a stagger above `gap / (0.7 · stage)` does not pile them, it
 * drives one THROUGH the other and the run comes out in the wrong order.
 * Measured: 0.254 for the outer pair, 0.230 for the inner. 0.15 leaves room. */
/* IT HAS TO TRACK STOP 2, and as a literal it did not. The word does not start
 * travelling until S2, so the moment its tail meets the leading print sits a
 * fixed distance AFTER S2 — not at a fixed camera position. Left at 1.35 while
 * the opening beat widened, the shove would have fired 0.45 early, mid-hold,
 * with the word still parked. Written as an offset it keeps the value it was
 * solved at (1.35 − 0.70) and moves with the beats from here on. */
const TAIL_OFFSET = 0.65; //        ... this far after the word starts travelling
const TAIL_AT = STOPS[2] + TAIL_OFFSET; // the leading print reaches its end
/* ON A NARROW SCREEN THERE IS NO SUCH MOMENT. `photographer` is 1.25 stage
 * wide on 16:9 and 2.63 on a 390px phone — more than two and a half
 * screenfuls — and the trio closes on its tail at only 0.7 stage per stage, so
 * the contact would land at cam 3.26, a full stage AFTER the `work` formation
 * has arrived. Solved rather than assumed, and the answer is that the ride is
 * a wide-screen idea: narrow keeps the old timing, where the shove starts the
 * moment the word does. */
const TAIL_NARROW = STOPS[2];
const PUSH_STAGGER = 0.15; //    camera between one print being caught and the next
const PUSH_RATE = 0.571; //      fraction of the clock — 1.4 × 0.571 = the word's 0.8

/* HOW THE WORK FORMATION MOVES, as opposed to where it ends up.
 *
 * Three things were making nine photographs arrive like dropped bricks, and
 * all three are about VELOCITY, not position:
 *
 *  1. they hit their slots at full speed and stopped in a single frame
 *     (measured −83px per notch → 0, nine times over)
 *  2. each wave stopped as a RANK — three prints freezing on the same frame
 *  3. the whole formation went 0 → full rate in one frame at the release
 *
 * `ARRIVE_EASE` is the deceleration window: inside it a print rides
 * `h(s) = 2s² − s³`, which is zero-speed at the landing and exactly parallax
 * speed at the far edge, so the glide has no seam where it begins.
 * `WAVE_STAGGER` shifts each print's own stop a little later than its LEFT
 * neighbour's, so a wave lands one photograph at a time.
 * `RELEASE_*` eases the exit back up to full rate on that same cubic.
 *
 * WHY THE STAGGER IS SAFE, and it is worth stating because the earlier version
 * of this was not. Every print in the formation shares one `aRate` and one
 * `aEase`, so `off` is the same monotone function of `gap` for all of them.
 * A print further right has a later stop, hence a larger gap, hence a larger
 * `off` — so for any pair, `gap(i,j) = Δhome + off(j) − off(i) ≥ Δhome` at
 * EVERY camera position. The prints can only ever be further apart than their
 * resting spacing, never closer. Give them different rates and that collapses,
 * which is what the old `1.55 / 1.40 / 1.25` needed a "leader settles last"
 * rule to patch around. */
const ARRIVE_EASE = 0.45; //   deceleration window, stage units
const WAVE_STAGGER = 0.06; //  camera between one print landing and the next
const RELEASE_AT = STOPS[5]; //     the leftmost print lets go here
const RELEASE_STAGGER = 0.05; //    ... and each one to its LEFT, this much later
const RELEASE_RAMP = 0.2; //        length of each print's own ease back to full rate

/* THE WALL LEAVES ONE PHOTOGRAPH AT A TIME, AND GATHERS AS IT GOES.
 *
 * Nine prints stepping from a drift to full parallax on the same frame is one
 * event, and it reads as a slab being dragged off rather than a wall being
 * panned past. Each print peels off `RELEASE_STAGGER` of camera after the one
 * to its RIGHT, and until its own moment arrives it keeps drifting with the
 * formation — `holdRate` continues the exact rate the anchors were producing,
 * so there is no seam at the hand-over from anchor-drift to clock-drift.
 *
 * RIGHT FIRST IS WHAT GATHERS THEM, and it is the same figure the opening trio
 * makes when the word's tail shoves it: a released print runs at 1.4 while its
 * left-hand neighbour is still creeping at 0.1, so the gap between them closes
 * at 1.3 per stage and the run bunches on its way out. Left-first is the safe,
 * dull alternative — every gap only ever opens, and the wall slides off in
 * formation.
 *
 * THE STAGGER HAS A CEILING, exactly as the trio's does. Closure over the
 * stagger must stay under the resting gap or a print does not gather, it
 * passes THROUGH its neighbour:
 *
 *     adjacent (different bands)  174px apart, 1 step  →  max 0.0885
 *     same band                   348px apart, 2 steps →  max 0.0885
 *
 * At 0.05 the same-band gap closes 348 → 151px, a 57% gather, against the
 * trio's 269 → 110 (59%). The two beats bunch by the same amount.
 *
 * There is room for it because the prints that have furthest to travel — the
 * right-hand ones — are the ones released FIRST, and the leftmost is already
 * at the frame edge when its turn comes. The contact board is not a deadline
 * either: it travels in from the right and first crosses the frame edge at cam
 * 4.13. One leaves as the other arrives, which is the handover the wall was
 * always supposed to end on. */
/* Built per-run from the live tune rather than read off the module consts, so
 * the panel can move RELEASE_AT and every plate's exit follows it. */
const exitAtFor = (T: Tune) => (n: number) => ({
  holdAt: T.RELEASE_AT + n * T.RELEASE_STAGGER,
  holdRate: T.HOLD_CLOCK_PRINT,
  drift: {
    to: T.RELEASE_AT + n * T.RELEASE_STAGGER + T.RELEASE_RAMP,
    ease: true,
    rate: T.HOLD_CLOCK_PRINT,
  },
});

/* The rate both giant words travel at.
 *
 * THE LEFT GROUP MUST NOT SHARE IT, and the reason is worth keeping. Give a
 * print the word's rate and its stop and the two hold a constant offset for
 * the whole approach — genuinely welded, measured at −273/−32/+245 px, steady
 * to 2px. It is also wrong, because a rigid assembly sliding in from the right
 * shows its LEFTMOST member first, and print 04 rests left of the word's own
 * left edge: it crossed into frame at 1.11 against the word's 1.37, so the
 * pictures announced the word instead of the other way round.
 *
 * The word has to arrive first AND nothing may settle before it, so the prints
 * have to move FASTER and land on the same frame the word pins. `aRate` is the
 * knob; the floor is `1468px / (S3·S − wordEntry) ≈ 1.04`, and PRINT_RATE
 * (1.4) clears it with room. They enter at 1.61 / 1.74 / 1.85 — after the word
 * — and all three stop dead at 2.30, exactly as the word does. Sharing one
 * rate and one stop across the three also freezes their gaps, so the group
 * cannot collide with itself no matter where it is in the approach. */
const WORD_RATE = 0.8;

/* How long `photographer` takes to get back up to speed after its freeze, in
 * stage units. A frozen element cannot simply resume — the clock would step
 * from 0 to 1:1 on one frame and the largest thing on screen would snap into
 * motion. Over this distance it accelerates from rest on the arrival cubic run
 * backwards, and has fully caught up by the end, so the softness costs it no
 * ground. Long enough to read as taking up the movement, short enough that the
 * first scroll after the paragraph lands visibly does something. */
const WORD_RELEASE_RAMP = 0.3;

/* Clear wall demanded between the two giant words, in stage units. It is the
 * margin in the width budget solved in `measureType` — the wall the section
 * wants between `photographer` leaving and `work` arriving, rather than the
 * zero that merely means "not yet overlapping". */
const WORD_GAP = 0.1;

/* One rate for EVERY print, and it is not a simplification — it is the whole
 * non-overlap guarantee. Give prints different rates by depth and their spacing changes as
 * the camera moves, so any two of them eventually slide into each other. Equal
 * rates freeze the gaps: lay the run out once without overlaps and it can never
 * develop one. Depth still varies blur, haze and shadow — just not speed. */
/* Where the wall stops being a desktop composition. It was 760, which put a
 * 744-wide iPad mini on the narrow layout and an 820-wide iPad on the DESKTOP
 * one — twelve prints across an 820 stage. Every tablet takes the narrow path. */
const NARROW = 1024;

const PRINT_RATE = 1.4;
/* A hold is expressed as a fraction of the element's OWN clock, and the two
 * layers travel at different rates, so the same drift on screen is a different
 * fraction for each. Deriving them from one number is the point: the type and
 * the photographs must creep together or the composition shears. */
const HOLD_CLOCK_PRINT = HOLD_DRIFT / PRINT_RATE; // 0.071
const HOLD_CLOCK_WORD = HOLD_DRIFT / WORD_RATE; //  0.125

interface Plate {
  src: string;
  ar: number; // image aspect (w/h) — the print is cropped to it
  ch: number; // which stop it belongs to
  x: number; // CENTRE, fraction of the stage, at its own stop
  y: number; // TOP of the frame, fraction of viewport height
  h: number; // image height, fraction of viewport height
  rot: number; // resting hang angle, degrees (never zero — prints hang crooked)
  pin: number; // where the pin sits along the top edge, 0..1
  d: number; // depth 0 (near) … 1 (far) — parallax, blur, haze
  len: number; // pendulum arm length multiplier — sets its swing period
  cap: string;
  loc: string;
  /** where this print MOVES TO by `nAt` — the wall re-arranges rather than
   *  simply sliding away. Screen-relative at that stop, same as x/y/h, so a
   *  print can shrink as it walks in.
   *
   *  Setting `n.x === x` PINS it: with a linear blend the two anchors cancel
   *  the parallax exactly, so the print holds a fixed screen position for the
   *  whole span. That is the entire sticky mechanic — no special case.
   *
   *  And setting `n.x` a little LEFT of `x` gives a slow drift instead of a
   *  dead pin — which the work formation uses, because a wall that answers
   *  nothing while you scroll does not feel attached to the scroll. The rate
   *  is `(x − n.x) / (nAt − ch)` in stage-per-stage and is INDEPENDENT of the
   *  element's own `rate`: the parallax terms cancel in the blend either way,
   *  which is why one number can hold the type and the photographs together. */
  n?: { x: number; y: number; h?: number };
  /** stop the second anchor lands on (default `ch + 1`). Spanning two stops is
   *  what lets a pin last a whole section rather than a single beat. */
  nAt?: number;
  /** stop until which it HOLDS after finishing its migration. Implemented by
   *  freezing the camera this element reads, not by pinning its target: aiming
   *  the second anchor at a fixed point instead makes a lagged print drift
   *  RIGHT at the end of its migration (the target stops receding while the
   *  print is still travelling, so the blend pulls it back). Freezing the clock
   *  keeps every derivative exactly as it was. */
  holdTo?: number;
  /** the same hold, in STAGE UNITS instead of stop indices — for a release that
   *  does not land on a stop. A pin has to end when the composition behind it
   *  is spent, and there is no reason for that to coincide with a camera stop;
   *  bending STOPS to hit it would drag the whole beat structure with it. */
  holdAt?: number;
  /** what happens after the hold and before full speed. Either the clock runs
   *  at a constant fraction (`rate`) until `to` — a CREEP, for something being
   *  carried at another element's speed — or it eases back up to full on the
   *  cubic (`ease`), which is the arrival's deceleration curve run backwards. */
  drift?: { to: number; rate?: number; ease?: boolean };
  /** fraction of the clock that runs DURING the hold. `0` (the default) is a
   *  dead freeze; anything above it makes the hold a slow drift, so the
   *  element still answers the wheel while it is nominally parked. */
  holdRate?: number;
  /** for the opening trio: `holdAt` and `drift.to` expressed as offsets from
   *  the TAIL CONTACT rather than as absolute camera positions, because that
   *  contact moves with the width of the word (see TAIL_NARROW). */
  tailPush?: number;
  tailExit?: number;
  /** migration window as a fraction of the span — `[start, length]`. Staggering
   *  these makes the opening trio travel at three different paces instead of
   *  moving as one rigid block. */
  nLag?: number;
  nSpan?: number;
  /** ── the approach, per print ──────────────────────────────────────────
   *  `aLead`  stage units added to its own stop = WHEN it lands. Stagger these
   *           and a group arrives one by one instead of as a rank.
   *  `aRate`  how fast it travels while still far away. Give the print that
   *           lands FIRST the highest rate: with the leaders faster than the
   *           followers the gaps between them only ever widen, so a staggered
   *           group can never collide during its approach.
   *  `aEase`  width of the deceleration window, in stage units. Inside it the
   *           print eases to a stop on h(s) = 2s² − s³ (s = distance/window),
   *           which is the cubic that has ZERO speed at landing and exactly
   *           parallax speed at the far edge — so it slows into place without
   *           a seam where the ease begins. */
  aLead?: number;
  aRate?: number;
  aEase?: number;
  /** present = this frame survives the mobile cut, with these overrides.
   *
   *  THE NARROW RUN IS SPACED TO THE FRAME, NOT SCALED FROM THE WIDE ONE. Six
   *  prints survive the cut and they were carrying desktop-derived positions —
   *  0.05 and 0.98 are sensible centres on a 1440 stage and are half off the
   *  edge of a 390 one, because a print is ~31% of a phone's width against ~9%
   *  of a desktop's. Measured before this: 02 was 37% cut at the opening, 04
   *  34% and 07 43% at the pin, so two of the four prints you look at while the
   *  formation is FROZEN were sliced by the frame.
   *
   *  The pinned four now run 0.18 / 0.39 / 0.61 / 0.82, which is the widest
   *  even spacing that keeps every print whole (a centre must sit within half a
   *  print of either edge), and they alternate top and bottom, so same-row
   *  neighbours are 0.43 apart — comfortably more than a print is wide. `n.x`
   *  tracks `x` because these are pinned; see HOLD_DRIFT. */
  m?: { x: number; y: number; h: number; n?: { x: number; y: number; h?: number } };
}

/* The table is built per-run from the live tune. Destructuring the rates it
 * refers to means the literal below is IDENTICAL to the one that used to sit at
 * module scope — the tuning panel changed how it is produced, never what it
 * says. `ov` is the panel's per-plate position overrides, keyed by `src`. */
const buildPlates = (T: Tune, ov?: PlateOverrides): Plate[] => {
  const { PRINT_RATE, ARRIVE_EASE, WAVE_STAGGER, HOLD_CLOCK_PRINT } = T;
  const exitAt = exitAtFor(T);
  const table: Plate[] = [
  /* ── 00 · the opening trio. Right of centre on load, walking in toward the
   *    middle across the WHOLE beat — no `nLag`, no `nSpan`, which is the
   *    solved answer rather than an omission (the derivation is at the window
   *    solver in layout). A compressed window makes a print travel RIGHT, and
   *    the compression that is safe depends on the length of the beat, so any
   *    authored pair is only correct until the beat moves. It moved.
   *
   *    THE THREE PACES ARE STILL THERE, and now they come from the composition
   *    instead of from a schedule: each print drifts left at −Δ/G, the distance
   *    it has to cover over the beat it has to cover it in. 01 travels 0.115
   *    stage, 02 0.169, 03 0.226 — so they read as −0.14 / −0.21 / −0.28, three
   *    speeds that are three DISTANCES. Nothing has to be kept in sync with
   *    anything, and the group cannot come apart.
   *
   *    They then ride `photographer` to its tail while it slides out
   *    from behind them and the About paragraph is read — and they let go the
   *    moment that composition is spent, not at the next camera stop. ── */
  { src: "01.jpg", ar: 0.667, ch: 0, tailPush: 0.3, tailExit: 0.45, holdRate: HOLD_CLOCK_PRINT, x: 0.412, y: 0.4, h: 0.4, rot: -7, pin: 0.3, d: 0.55, len: 1.15, cap: "Two Figures, Backlit", loc: "Berlin · 2021",
    n: { x: 0.297, y: 0.52, h: 0.29 }, m: { x: 0.4, y: 0.48, h: 0.3, n: { x: 0.2, y: 0.5, h: 0.26 } } },
  { src: "02.jpg", ar: 0.753, ch: 0, tailPush: 0.15, tailExit: 0.48, holdRate: HOLD_CLOCK_PRINT, x: 0.656, y: 0.15, h: 0.36, rot: 4, pin: 0.56, d: 0.12, len: 0.85, cap: "Red Room, No. 4", loc: "Lisbon · 2022",
    n: { x: 0.487, y: 0.185, h: 0.265 }, m: { x: 0.8, y: 0.16, h: 0.28, n: { x: 0.66, y: 0.18, h: 0.25 } } },
  { src: "03.jpg", ar: 0.8, ch: 0, tailPush: 0.0, tailExit: 0.51, holdRate: HOLD_CLOCK_PRINT, x: 0.916, y: 0.25, h: 0.38, rot: 9, pin: 0.44, d: 0.34, len: 1.0, cap: "Blue Hour Portrait", loc: "Trieste · 2023",
    n: { x: 0.69, y: 0.43, h: 0.28 } },

  /* ── the PINNED formation. Every print below is anchored to beat 3 with
   *    `n.x === x` and `nAt: 5`, so it holds a fixed screen position for the
   *    whole pin — one frozen wall that slides off as a unit when beat 5
   *    releases. `aLead`/`aRate`/`aEase` stagger the approach so a group lands
   *    one by one; what else differs is WHERE each sits, and therefore when its
   *    leading edge crosses into the frame:
   *      ch 3 → the LEFT group, which travels in WITH `work` and is hung by
   *             the time the word comes to rest. `aLead` is NEGATIVE here, and
   *             that is the whole point: a group that lands after the word it
   *             belongs to has already missed its moment. Landings 2.30 / 2.45
   *             / 2.60 against a word that is fully on screen at 2.30 and
   *             pinned at 2.60 — you read `work` and the three prints are
   *             already on it. Left at the old +0.8/0.95/1.1 they settled at
   *             3.40/3.55/3.70: a full stage of pinned word over bare wall,
   *             with the prints arriving long after anyone had finished
   *             looking.
   *      ch 4 → the RIGHT group, which must not arrive until the word is
   *             whole. It enters at 3.00 and lands 3.30 / 3.50 / 3.70 — after
   *             the word is fully readable (2.30) AND after the left group has
   *             settled (2.60), so the two halves of the formation read as two
   *             events rather than one shower of pictures.
   *             Anchoring it a beat LATER than the wall it joins is what lets
   *             it TRAVEL: before its own stop it is simply approaching at
   *             print rate from off-frame right, it lands at beat 4, and only
   *             then does `nAt` pin it with the rest. Approach → pin →
   *             release, all out of the same two anchors. Anchor it with the
   *             wall instead and it can only pop into place, because a pinned
   *             print has nowhere to travel from.
   *    No visibility gate anywhere: every print is always mounted and always
   *    positioned, and geometry alone keeps it off-frame until its own beat.
   *    MOBILE cuts the right-hand group entirely. A phone has room for two
   *    vertical bands and the left group already occupies both, so every free
   *    slot left is to the LEFT of a pinned print in the same band — an
   *    arriving print would have to travel straight through it (measured 66px
   *    of overlap). Where a layout cannot be made to fit, drop the content;
   *    do not shrink it until the collision is merely small.
   *
   *    A GROUP'S RESTING PLACE AND ITS STAGGER ARE COUPLED. Sliding the pinned
   *    trio right to centre it (+0.141) cuts each print's leftward travel by
   *    the same amount, and `dx/dcam = −rate + D/(nSpan·gap)` then flips
   *    POSITIVE for the most-lagged one — it would drift right at the end of
   *    its migration. Widening the window (0.66 → 0.85) restores the margin at
   *    the cost of a tighter stagger. Move a group and re-check the sign; the
   *    two numbers cannot be tuned apart.
   *
   *    ORDER MATTERS in a staggered group: whichever print settles FIRST must
   *    not be the one furthest LEFT. A print that decelerates while the one
   *    behind it is still at full speed gets caught — so the leader has to keep
   *    moving longest. Hence the descending lags on the opening trio and the
   *    descending `aRate`s here: leaders fast and late to settle, followers
   *    slower and earlier, which makes every gap widen instead of close.
   *
   *    Nothing is hung after the release: the wall ends on these NINE, which
   *    slide off together and hand the frame straight to the contact board.
   *
   *    THE FORMATION IS TWO BANDS AND THE ORDER OF ARRIVAL IS FORCED BY THEM.
   *    Nine prints alternate high/low across x 0.05 → 0.97 on a 0.115 stride,
   *    so same-band neighbours sit 0.23 apart — comfortably clear of the
   *    widest print (0.184 stage, the landscape one on a tall window) plus the
   *    inflation its tilt adds to the box.
   *
   *    Within a band a print must land LEFT of everything already pinned
   *    there. An arrival travels in from off-frame right and sweeps every x
   *    greater than its own, so a slot that has a pinned print to its right is
   *    not reachable — the arrival would have to pass through it. Hence three
   *    waves left-to-right (2.30 / 2.75 / 3.20) rather than three groups
   *    scattered across the wall, and hence a wave can only ever be appended
   *    to the RIGHT of the last one. Adding a print anywhere else means
   *    re-cutting the whole run.
   *
   *    Every print shares ONE rate and ONE ease window, and each one's stop is
   *    later than that of the print to its LEFT — which is what makes the
   *    whole run provably non-colliding at every camera position, not just at
   *    rest. See ARRIVE_EASE / WAVE_STAGGER above for the argument.
   *
   *    `x` is NOT the design position: it is the design position minus the
   *    drift the formation has already accumulated by the time this print
   *    lands. See HOLD_DRIFT. ── */
  /* WAVE 1 — lands 2.30, the frame `work` pins. */
  { src: "04.jpg", ar: 0.753, ch: 3, nAt: 5, aRate: PRINT_RATE, aLead: 0, aEase: ARRIVE_EASE, ...exitAt(8), x: 0.05, y: 0.08, h: 0.28, rot: -5, pin: 0.6, d: 0.3, len: 0.9, cap: "Prism Across a Face", loc: "Studio · 2023", n: { x: 0.05, y: 0.08 }, m: { x: 0.18, y: 0.16, h: 0.28, n: { x: 0.18, y: 0.16 } } },
  { src: "05.jpg", ar: 0.8, ch: 3, nAt: 5, aRate: PRINT_RATE, aLead: WAVE_STAGGER, aEase: ARRIVE_EASE, ...exitAt(7), x: 0.165, y: 0.46, h: 0.26, rot: 7, pin: 0.38, d: 0.1, len: 1.1, cap: "Field, Long Exposure", loc: "Ronda · 2020", n: { x: 0.165, y: 0.46 }, m: { x: 0.39, y: 0.48, h: 0.26, n: { x: 0.39, y: 0.48 } } },
  { src: "06.jpg", ar: 0.75, ch: 3, nAt: 5, aRate: PRINT_RATE, aLead: 2 * WAVE_STAGGER, aEase: ARRIVE_EASE, ...exitAt(6), x: 0.28, y: 0.07, h: 0.27, rot: -9, pin: 0.5, d: 0.5, len: 0.95, cap: "Dune Study", loc: "Erg Chigaga · 2019", n: { x: 0.28, y: 0.07 }, m: { x: 0.61, y: 0.16, h: 0.27, n: { x: 0.61, y: 0.16 } } },

  /* WAVE 2 — lands 2.75, into the slots immediately right of wave 1. */
  { src: "10.jpg", ar: 1.0, ch: 4, nAt: 5, aLead: -0.25, aRate: PRINT_RATE, aEase: ARRIVE_EASE, ...exitAt(5), x: 0.395, y: 0.44, h: 0.24, rot: 5, pin: 0.45, d: 0.42, len: 1.15, cap: "Violet, In Passing", loc: "Copenhagen · 2021", n: { x: 0.395, y: 0.44 } },
  { src: "07.jpg", ar: 1.327, ch: 4, nAt: 5, aLead: -0.25 + WAVE_STAGGER, aRate: PRINT_RATE, aEase: ARRIVE_EASE, ...exitAt(4), x: 0.51, y: 0.1, h: 0.21, rot: 5, pin: 0.4, d: 0.6, len: 1.25, cap: "Comet in the Iris", loc: "Studio · 2024", n: { x: 0.51, y: 0.1 }, m: { x: 0.82, y: 0.49, h: 0.17, n: { x: 0.82, y: 0.49 } } },
  { src: "11.jpg", ar: 1.0, ch: 4, nAt: 5, aLead: -0.25 + 2 * WAVE_STAGGER, aRate: PRINT_RATE, aEase: ARRIVE_EASE, ...exitAt(3), x: 0.625, y: 0.47, h: 0.25, rot: -6, pin: 0.52, d: 0.18, len: 0.92, cap: "Two Hands, One Beam", loc: "Studio · 2020", n: { x: 0.625, y: 0.47 } },

  /* WAVE 3 — lands 3.20, and fills the wall out to the right edge. */
  { src: "08.jpg", ar: 0.753, ch: 4, nAt: 5, aLead: 0.2, aRate: PRINT_RATE, aEase: ARRIVE_EASE, ...exitAt(2), x: 0.74, y: 0.08, h: 0.27, rot: -4, pin: 0.55, d: 0.25, len: 0.88, cap: "The Arch at Dawn", loc: "Meteora · 2018", n: { x: 0.74, y: 0.08 } },
  { src: "12.jpg", ar: 0.8, ch: 4, nAt: 5, aLead: 0.2 + WAVE_STAGGER, aRate: PRINT_RATE, aEase: ARRIVE_EASE, ...exitAt(1), x: 0.855, y: 0.45, h: 0.26, rot: 8, pin: 0.42, d: 0.36, len: 1.05, cap: "Amber, Turning", loc: "Marseille · 2022", n: { x: 0.855, y: 0.45 } },
  { src: "09.jpg", ar: 1.0, ch: 4, nAt: 5, aLead: 0.2 + 2 * WAVE_STAGGER, aRate: PRINT_RATE, aEase: ARRIVE_EASE, ...exitAt(0), x: 0.97, y: 0.15, h: 0.24, rot: 8, pin: 0.35, d: 0.05, len: 1.05, cap: "Hand, One Bulb", loc: "Studio · 2022", n: { x: 0.97, y: 0.15 } },

  ];
  if (!ov) return table;
  /* A pinned plate has `n.x === x` by construction (see HOLD_DRIFT). Dragging
   * `x` in the panel has to carry `n.x` with it or the plate silently starts
   * drifting again — so the second anchor tracks the first unless the panel
   * moved it explicitly. */
  return table.map((p) => {
    const o = ov[p.src];
    if (!o) return p;
    const pinned = p.n !== undefined && p.n.x === p.x;
    const x = o.x ?? p.x;
    const y = o.y ?? p.y;
    const n = p.n
      ? { ...p.n, x: o.nx ?? (pinned ? x : p.n.x), y: o.ny ?? (pinned ? y : p.n.y) }
      : p.n;
    return { ...p, x, y, h: o.h ?? p.h, rot: o.rot ?? p.rot, n };
  });
};

/** The headline ribbon. `x` is the LEFT edge in stage fractions at its own stop;
 *  words are free to run off both sides — you're meant to read them in passing. */
interface Word {
  text: string;
  ch: number;
  x: number;
  size: number; // fraction of viewport height
  base: number; // baseline, fraction of viewport height
  /** extra stops this word stays at full strength — the intro word carries
   *  through the re-arrangement beat, because it's still the same scene */
  hold?: number;
  /** baseline it RISES to by the next stop. The intro word starts sitting on
   *  the floor of the section and climbs to the middle as the wall re-arranges. */
  nBase?: number;
  /** left edge it TRAVELS to by the next stop. Negative parallax, in effect:
   *  the camera pans right, the word walks right against it. */
  nX?: number;
  /** size it SHRINKS to by the next stop — the headline recedes as the prints
   *  do, so the whole opening reads as one scene settling back */
  nSize?: number;
  /** stops BEFORE `ch` that it is already visible — a word anchored to the
   *  middle of its run enters from the right, centres, and exits left */
  pre?: number;
  /** its own parallax rate. The opening word wants to CLEAR fast once its half
   *  of the section is over; the closing word wants to LINGER across three
   *  beats. One rate cannot do both, so a word carries its own. */
  rate?: number;
  /** stop the second anchor lands on (default `ch + 1`). With `nX === x` this
   *  PINS the word — it holds a fixed screen position across the whole span
   *  while photographs are hung around it. */
  nAt?: number;
  /** stop until which it HOLDS after reaching its second anchor — the same
   *  frozen-clock trick the prints use. */
  holdTo?: number;
  /** fraction of the clock that runs during the hold — see the Plate field */
  holdRate?: number;
  /** how the word LEAVES its hold, in the plates' own terms. Without it the
   *  clock steps from the hold rate straight to 1:1 on a single frame, which a
   *  frozen word cannot survive: it stands still, then is suddenly travelling
   *  at full rate. `ease` runs the arrival cubic backwards from whatever rate
   *  the word is already moving at (`rate` — 0 for a dead freeze), and has
   *  fully caught up by `to`, so a soft start costs the word no ground. */
  drift?: { to: number; ease?: boolean; rate?: number };
}
/* Same arrangement as `buildPlates` — the literal is unchanged, only how it is
 * produced. `nX === x` is what pins a word, so the panel's `x` carries `nX` for
 * a pinned word exactly as it carries `n.x` for a pinned plate. */
const buildWords = (T: Tune, ov?: WordOverrides): Word[] => {
  const { WORD_RATE, STOPS, WORD_RELEASE_RAMP } = T;
  const table: Word[] = [
  /* `photographer` — fills the frame on load, then walks RIGHT and shrinks so
   * a whole fragment reads. Solved from the measured glyph advances at size
   * 0.45 (p 0 · h .163 · o .329 · t .483 · o .582 · g .737 · r .885 · a 1.010),
   * which scale linearly with size. Three rules, none of them tunable:
   *
   *  1. Land a glyph BOUNDARY exactly on the frame edge. A glyph's start IS the
   *     previous glyph's end, so anything else slices the first visible letter.
   *
   *  2. Position and SIZE are ONE decision. Each step right lands on the next
   *     glyph and adds that glyph's width to what must still fit on the right,
   *     so moving right eventually pushes the tail off unless the word also
   *     shrinks. `o`-flush at size 0.40 overruns (right edge 1.036); the same
   *     anchor at 0.38 fits (0.984). Solve the pair, never one at a time.
   *
   *  3. Leave HEADROOM for where the camera actually rests. There is no magnet,
   *     so a visitor sits anywhere near a beat, and past it the word drifts left
   *     at `rate`. A `g`-flush anchor is exact at the stop and clipped a hair
   *     past it — being exact only at the stop is being wrong nearly everywhere
   *     else. `o`-flush buys ~283px of drift before the first letter is touched.
   *
   *   load      x −0.750, size  0.45 → "grapher" fills the frame edge to edge
   *   scrolled  nX  0.000, nSize 0.35 → the word runs ALL the way right, until
   *             its own origin sits on the frame edge and you read it from the
   *             beginning: "photograp…", tail off the right
   *
   * `nX = 0` is the far end of that travel and it is the one anchor that needs
   * no glyph table — the word's origin IS a boundary, so nothing can be sliced.
   * Every anchor short of it (t/o/g-flush) shows the word mid-way through and
   * costs size to keep the tail on screen; going all the way right instead buys
   * legibility for free and gives the motion somewhere to arrive.
   *
   * It then HOLDS through the about beat (`holdTo: 2`) and only afterwards
   * travels LEFT — so the sequence reads right → still → left, not one
   * continuous drift. */
  {
    text: "photographer",
    ch: 0,
    hold: 2,
    rate: WORD_RATE,
    x: -0.75,
    size: 0.45,
    base: 0.8,
    nAt: 1,
    holdTo: 2,
    /* NO `holdRate` — a dead freeze, and the absence is the point.
     *
     * It used to hold at HOLD_CLOCK_WORD, so through the whole beat where the
     * trio settles and the paragraph rises, the giant word was creeping left at
     * 0.1 stage per stage. Small, but it is the largest thing on the screen:
     * whatever it does is what the section looks like it is doing, and it meant
     * the eye never got the beat of stillness the sequence is built on. The
     * word is now motionless from the moment the trio lands until the paragraph
     * is fully in, and the ONLY thing moving in that stretch is the paragraph
     * itself.
     *
     * The trio keeps its own HOLD_DRIFT so the wheel is never unanswered — it
     * slides ~72px against the frozen word across the hold, which reads as the
     * prints settling rather than as the composition coming apart. */
    drift: { to: STOPS[2] + WORD_RELEASE_RAMP, ease: true, rate: 0 },
    nX: 0,
    nSize: 0.35,
    /* MASS on the centre line, not the baseline: `baseline − 0.25em`, which at
     * nSize 0.35 is 0.5875 — not 0.62. Getting it right does double duty, since
     * the word then reads as centred AND its descenders stop ~30px higher,
     * which is most of the clear air the paragraph beneath it needs. Descenders
     * are what closes that gap, never the baseline. */
    nBase: 0.5875,
  },
  /* `work` — PINNED. `nX === x` with `nAt` two stops out makes the two anchors
   * cancel the parallax exactly, so it holds one screen position from beat 3 to
   * beat 5 while photographs are hung around it, then releases and leaves.
   *
   * Its rate (0.8) is slower than `photographer`'s (0.9) on purpose: with the
   * outgoing word the faster of the two, the gap between them only ever widens,
   * so they can share the screen during the handoff without ever overlapping.
   * Closest approach is 0.43 stage — one at the left edge, one at the right. */
  {
    text: "work",
    ch: 3,
    pre: 3,
    hold: 3,
    rate: WORD_RATE,
    x: 0.21,
    size: 0.4,
    base: 0.62,
    nAt: 5,
    /* `nX === x` — the two anchors cancel the parallax EXACTLY, so the word is
     * genuinely nailed to one screen position from beat 3 to beat 5 and only
     * moves when the formation releases. It used to sit 0.14 left of `x` at the
     * release, which is HOLD_DRIFT × (RELEASE − S3): the word crept the whole
     * time the photographs were arriving. Held means held. */
    nX: 0.21,
  },
  ];
  if (!ov) return table;
  return table.map((w) => {
    const o = ov[w.text];
    if (!o) return w;
    const pinned = w.nX !== undefined && w.nX === w.x;
    const x = o.x ?? w.x;
    return {
      ...w,
      x,
      size: o.size ?? w.size,
      base: o.base ?? w.base,
      nX: o.nX ?? (pinned ? x : w.nX),
      nSize: o.nSize ?? w.nSize,
      nBase: o.nBase ?? w.nBase,
    };
  });
};

/* Parallax rates. The PHOTOGRAPHS are the fast layer (1.0) and the giant type
 * is slow (0.7 / 0.38 per word), which is the conventional arrangement and the
 * one that actually reads here: the word behaves like paint on the wall itself
 * while the prints skate across in front of it. The pictures lead the movement,
 * the type anchors it — so a scroll feels like travelling past photographs
 * rather than dragging a poster sideways. */
const TYPE_RATE = 1.0;
/* The two words must never be on screen together, and with slow type that is a
 * WIDTH problem, not a rate problem: `photographer` is ~1.3 stage wide, so at
 * rate 0.7 it needs 1.9 stage of travel just to clear the left edge — by which
 * point `work` has already entered from the right. Narrowing the word is what
 * buys the separation; speeding it up would make it outrun the photographs,
 * which is the one thing this section must not do. */
/* NOTHING here reacts to a print scrolling into view. No fade, no develop, no
 * swing-on-arrival, no visibility gate — a photograph is simply on the wall,
 * fully formed, and you travel past it. Every print enters by TRAVELLING in
 * from off-frame right, which the layout already guarantees (each one's leading
 * edge crosses x=1.0 exactly on its own beat), so geometry does the whole job
 * that an arrival gate used to. The pendulum stays, but only for what the
 * VISITOR does: sweeping the cursor through a cluster, or grabbing a print. */

/* pendulum constants — tuned so a hard fling swings ~10° and rings down in ~2s */
const K_SPRING = 0.024; // restoring — sets the period (2π/√k ≈ 40 frames)
const K_DAMP = 0.075; // air
const K_CURSOR = 0.00006; // how hard a cursor sweep shoves them
const MAX_SWING = 0.5; // rad, hard stop either side of rest

/* ─────────────────────────── the tuning surface ───────────────────────────
 *
 * Every number above that is a JUDGEMENT rather than a derivation, collected
 * into one object so the dev panel can drive them and the engine can be rebuilt
 * from a new set without a reload. The declarations above remain the defaults
 * AND the documentation — this only gathers them.
 *
 * The consts stay where they are on purpose. Their comments are the argument for
 * the value, and an argument separated from the number it defends rots. Nothing
 * here is a second source of truth: `DEFAULT_TUNE` reads the consts, and the
 * derived fields are recomputed by `buildTune` from whatever the panel supplied,
 * so moving a base carries its dependents with it (change `STOPS[5]` and
 * `RELEASE_AT` follows; change `HOLD_DRIFT` and both hold clocks follow).
 *
 * NOT tunable, deliberately: the glyph-boundary anchors for `photographer`
 * (position and size are one solved decision — see the Word table), the plate
 * `pin`/`d`/`len`/`ar` physics identity, and anything the layout solves per
 * breakpoint. Those are constraints, not preferences. */
export interface Tune {
  STOPS: number[];
  HOLD_DRIFT: number;
  TAIL_OFFSET: number;
  TAIL_AT: number;
  TAIL_NARROW: number;
  PUSH_STAGGER: number;
  PUSH_RATE: number;
  ARRIVE_EASE: number;
  WAVE_STAGGER: number;
  RELEASE_AT: number;
  RELEASE_STAGGER: number;
  RELEASE_RAMP: number;
  WORD_RATE: number;
  WORD_RELEASE_RAMP: number;
  PRINT_RATE: number;
  TYPE_RATE: number;
  HOLD_CLOCK_PRINT: number;
  HOLD_CLOCK_WORD: number;
  NARROW: number;
  SCROLL: number;
  EASE: number;
  K_SPRING: number;
  K_DAMP: number;
  K_CURSOR: number;
  MAX_SWING: number;
}

export type PlateOverrides = Record<
  string,
  { x?: number; y?: number; h?: number; rot?: number; nx?: number; ny?: number }
>;
export type WordOverrides = Record<
  string,
  { x?: number; size?: number; base?: number; nX?: number; nSize?: number; nBase?: number }
>;
export interface Overrides {
  tune?: Partial<Tune>;
  plates?: PlateOverrides;
  words?: WordOverrides;
}

export const DEFAULT_TUNE: Tune = {
  STOPS,
  HOLD_DRIFT,
  TAIL_OFFSET,
  TAIL_AT,
  TAIL_NARROW,
  PUSH_STAGGER,
  PUSH_RATE,
  ARRIVE_EASE,
  WAVE_STAGGER,
  RELEASE_AT,
  RELEASE_STAGGER,
  RELEASE_RAMP,
  WORD_RATE,
  WORD_RELEASE_RAMP,
  PRINT_RATE,
  TYPE_RATE,
  HOLD_CLOCK_PRINT,
  HOLD_CLOCK_WORD,
  NARROW,
  SCROLL: 2.6, // px of wall travel per px of wheel delta
  /* Camera smoothing — the per-frame fraction of the remaining distance the
   * camera closes. It runs BACKWARDS to the word: a SMALLER number is MORE
   * smoothing, because the camera commits less of the gap per frame and so
   * takes longer to arrive. The panel exposes it as a percentage (1 − EASE) to
   * match how a scroll-smoothing control is normally labelled.
   *
   * At 0.30 every wheel notch lands as a discrete step — correct, but it reads
   * as clicking rather than travelling. 0.18 blended the notches into one
   * glide (~91% of the way there in 0.2s). 0.09 doubles that settle to ~0.4s,
   * which is where the common smooth-scroll libraries sit (Lenis ships 0.1),
   * and is the point where the wall stops feeling like it is answering
   * individual notches at all.
   *
   * WHAT THIS IS NOT, AND MUST NOT BECOME. Smoothing here is an exponential
   * approach to where the wheel already put the target: monotonic, and it
   * arrives and stops. It is not momentum. There is no magnet and no coast —
   * releasing the wheel does not add distance, it only takes ~0.4s to finish
   * the distance you already asked for. The coast people hate comes from a
   * magnet, and adding one to chase a smoother feel would break the one thing
   * this section promises about its camera: the wall stops where you stopped.
   *
   * IT COUPLES TO HOVER. A print only lifts while the wall is at rest
   * (|vel| < 0.6 px/frame), so a slower camera means hover engages later after
   * you stop — measured ~0.36s at 0.18 against ~0.57s at 0.09. Push the
   * smoothing much past this and the delay starts reading as an unresponsive
   * print rather than a settling wall. */
  EASE: 0.09,
  K_SPRING,
  K_DAMP,
  K_CURSOR,
  MAX_SWING,
};

/** Merge the panel's overrides over the defaults, then RE-DERIVE everything that
 *  is a function of something else. A panel that let you set `RELEASE_AT` to one
 *  thing and `STOPS[5]` to another would be lying about what the wall does. */
export function buildTune(ov?: Partial<Tune>): Tune {
  const t: Tune = { ...DEFAULT_TUNE, ...ov };
  t.TAIL_AT = t.STOPS[2] + t.TAIL_OFFSET;
  t.TAIL_NARROW = t.STOPS[2];
  t.RELEASE_AT = t.STOPS[5];
  t.HOLD_CLOCK_PRINT = t.HOLD_DRIFT / t.PRINT_RATE;
  t.HOLD_CLOCK_WORD = t.HOLD_DRIFT / t.WORD_RATE;
  return t;
}

/* ─────────────────────────── component ─────────────────────────── */

/* The wall runs on the declared defaults. Built once at module scope: nothing
 * changes it at runtime, so the engine effect never has to rebuild. */
const CFG = (() => {
  const T = buildTune();
  return { T, plates: buildPlates(T), words: buildWords(T) };
})();

export default function Emulsion({ className }: { className?: string }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const typeRef = useRef<HTMLCanvasElement>(null);
  const grainRef = useRef<HTMLCanvasElement>(null);
  const wallRef = useRef<HTMLDivElement>(null);

  const [theme, setTheme] = useState<"light" | "dark">("light");
  const pal = theme === "dark" ? DARK : LIGHT;

  /* chapter index drives only the chrome (counter + nav underline) */
  const [chapter, setChapter] = useState(0);
  /* The prints start transparent for the load fade. This CANNOT be an inline
   * style in the JSX: React re-applies its props on every re-render, and
   * `chapter` changes as you scroll — so the rAF would clear the opacity and
   * the next re-render would set it straight back to 0, blinking the entire
   * wall out mid-section. Keep it in the stylesheet behind a root attribute and
   * retire the attribute through state, so React and the rAF never disagree.
   * ALWAYS false to begin with — never seeded from reduced-motion. The engine
   * runs from an effect, i.e. after React has already painted, so a wall that
   * starts visible paints its nine prints un-transformed in the top-left corner
   * and snaps them onto their pins a frame later (measured: 1114px). Reduced
   * motion is a request for no ANIMATION, not for the raw first frame. The
   * attribute costs one hidden frame and the loop clears it immediately. */
  const [introDone, setIntroDone] = useState(false);

  /* the contact form is plain React state — the engine never touches it */
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState("");

  /* the message is posted to /api/contact, which files it in the
   * `contact_submissions` collection. The engine is untouched by any of it. */
  const submitContact = async (e: FormEvent) => {
    e.preventDefault();
    if (sending) return;

    const name = form.name.trim();
    const email = form.email.trim();

    if (!name) return setFormError("Please add your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return setFormError("That email address looks incomplete.");
    }

    setFormError("");
    setSending(true);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, note: form.note.trim() }),
      });
      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        setFormError(data?.error ?? "The message did not go through. Try again in a moment.");
        return;
      }

      setSent(true);
    } catch {
      setFormError("The message did not go through. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  /* the engine exposes one setter so the nav can drive the camera */
  const gotoRef = useRef<(ch: number) => void>(() => {});

  useEffect(() => {
    setTheme(readTheme());
    const mo = new MutationObserver(() => setTheme(readTheme()));
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    /* Only the names the body actually reads — the rest reaches the engine
     * already baked into the two tables (rates, hold clocks and every
     * `exitAt` window are resolved by `buildPlates`/`buildWords`). */
    const {
      STOPS, TAIL_AT, TAIL_NARROW, PUSH_RATE, PRINT_RATE, TYPE_RATE, NARROW,
      SCROLL, EASE, K_SPRING, K_DAMP, K_CURSOR, MAX_SWING,
    } = CFG.T;
    const PLATES = CFG.plates;
    const WORDS = CFG.words;

    const root = rootRef.current;
    const typeCv = typeRef.current;
    const grainCv = grainRef.current;
    const wall = wallRef.current;
    if (!root || !typeCv || !grainCv || !wall) return;
    const tctx = typeCv.getContext("2d");
    const gctx = grainCv.getContext("2d");
    if (!tctx || !gctx) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* canvas can't read a CSS var — resolve the loaded family string */
    const cs = getComputedStyle(document.documentElement);
    const SERIF =
      cs.getPropertyValue("--font-playfair").trim() ||
      "'Playfair Display', Georgia, serif";

    /* ── geometry ── */
    let W = root.clientWidth || 1;
    let H = root.clientHeight || 1;
    let mobile = W < NARROW;
    /* the composition is laid out on a STAGE, not the raw viewport, so an
     * ultrawide window adds margin instead of pulling the cluster apart */
    let stage = Math.min(W, H * 1.85);
    let tailAt = TAIL_AT;
    let vBasis = Math.min(H, stage * 0.66);
    let vTop = (H - vBasis) / 2;
    let stageX = (W - stage) / 2;
    let dpr = Math.min(2, window.devicePixelRatio || 1);

    /* ── the camera ── */
    let cam = 0;
    let camTarget = 0;
    let camPrev = 0;
    let vel = 0;
    let maxCam = STOPS[STOPS.length - 1] * stage;
    let tick = 0;

    const stopPx = (ch: number) => STOPS[clamp(ch, 0, STOPS.length - 1)] * stage;

    /* ── plate runtime ── */
    interface P {
      def: Plate;
      el: HTMLElement;
      inner: HTMLElement;
      card: HTMLElement;
      img: HTMLImageElement;
      i: number;
      live: boolean; // survives the mobile cut
      fw: number;
      fh: number;
      homeX: number;
      homeY: number;
      /** the second pin — where this print re-hangs by the next stop (or null) */
      homeX2: number | null;
      homeY2: number;
      /** migration window, SOLVED at layout from the beat length — see below */
      lagV: number;
      spanV: number;
      /** and at what size, relative to its first — it can shrink on the way */
      nScale: number;
      pinPx: number;
      rate: number;
      base: number; // resting angle, rad
      ang: number;
      holdAtV: number | undefined;
      driftV: { to: number; rate?: number; ease?: boolean } | undefined;
      angVel: number;
      hover: number;
      hoverT: number;
      grab: boolean;
      grabOff: number;
      cx: number; // cached centre, for proximity
      cy: number;
      shadowKey: number;
    }
    const plates: P[] = [];

    const nodes = Array.from(
      wall.querySelectorAll<HTMLElement>("[data-plate]")
    );
    nodes.forEach((el, i) => {
      const def = PLATES[i];
      const inner = el.querySelector<HTMLElement>("[data-inner]")!;
      const card = el.querySelector<HTMLElement>("[data-card]")!;
      const img = el.querySelector<HTMLImageElement>("img")!;
      plates.push({
        def, el, inner, card, img, i,
        live: true,
        fw: 0, fh: 0, homeX: 0, homeY: 0, homeX2: null, homeY2: 0, lagV: 0, spanV: 1, nScale: 1, pinPx: 0,
        rate: PRINT_RATE,
        base: (def.rot * Math.PI) / 180,
        ang: (def.rot * Math.PI) / 180,
        angVel: 0,
        holdAtV: undefined,
        driftV: undefined,
        hover: 0, hoverT: 0,
        grab: false, grabOff: 0,
        cx: 0, cy: 0, shadowKey: -1,
      });
    });

    /* ── copy blocks + the contact card read their placement off the DOM ── */
    interface Block {
      el: HTMLElement;
      ch: number;
      x: number;
      y: number;
      w: number;
      /* a narrow viewport gets its own column — same words, different wall */
      xm: number;
      ym: number;
      wm: number;
      /** rises into place from `lift × H` below its resting line as its beat
       *  arrives — the note about the work climbs up from under the fold
       *  rather than merely sliding in from the right with the wall */
      lift: number;
      /** how far before its stop the reveal window opens, and how long it
       *  spans, in stage fractions. Copy wants this LATE and narrow (one
       *  paragraph at a time); the contact board wants it early, so the board
       *  slides in from the right as the last prints slide out to the left and
       *  there is never a stretch of bare wall between them. */
      lead: number;
      span: number;
      /** freeze the sideways travel through this many extra stops */
      hold: number;
      /** hold end in STAGE UNITS — overrides `hold` when set */
      holdAt: number;
      /** hold end expressed as an offset from the tail contact — overrides both */
      holdTail: number | null;
      cx: number; // resolved for the current breakpoint
      cy: number;
      rate: number;
      lines: HTMLElement[];
    }
    const num = (v: string | undefined, fb: number) =>
      v === undefined ? fb : Number(v);
    const blocks: Block[] = Array.from(
      wall.querySelectorAll<HTMLElement>("[data-block]")
    ).map((el) => {
      const x = Number(el.dataset.x);
      const y = Number(el.dataset.y);
      const w = Number(el.dataset.w);
      return {
        el,
        ch: Number(el.dataset.ch),
        x, y, w,
        xm: num(el.dataset.xm, x),
        ym: num(el.dataset.ym, y),
        wm: num(el.dataset.wm, w),
        lift: num(el.dataset.lift, 0),
        lead: num(el.dataset.lead, 0.09),
        span: num(el.dataset.span, 0.14),
        hold: num(el.dataset.hold, 0),
        holdAt: num(el.dataset.holdat, 0),
        holdTail: el.dataset.holdtail === undefined ? null : Number(el.dataset.holdtail),
        cx: x,
        cy: y,
        rate: Number(el.dataset.rate || 1),
        lines: Array.from(el.querySelectorAll<HTMLElement>("[data-line] > *")),
      };
    });

    /* ── glyph runtime for the headline ribbon ── */
    interface G {
      ch: string;
      w: number;
      off: number; // x offset within the word — that is the whole glyph state
    }
    interface Wd {
      def: Word;
      glyphs: G[];
      rate: number;
      size: number;
      sizeN: number;
      base: number;
      baseN: number;
      homeX: number;
      homeXN: number | null;
    }
    const words: Wd[] = WORDS.map((def) => ({
      def,
      glyphs: [],
      rate: def.rate ?? TYPE_RATE,
      size: 0,
      sizeN: 0,
      base: 0,
      baseN: 0,
      homeX: 0,
      homeXN: null,
    }));

    /* ── layout: everything that depends on viewport size ── */
    function layout() {
      W = root!.clientWidth || 1;
      H = root!.clientHeight || 1;
      mobile = W < NARROW;
      /* ONE VERTICAL BASIS for both size and position.
       * Print heights were already clamped to `min(H, stage·0.66)`, and that
       * clamp is the horizontal non-overlap guarantee — it is not the problem.
       * POSITIONS were a fraction of H, and that is: the moment H ran ahead of
       * the stage the composition stretched while its contents did not. On an
       * 820×1180 tablet the two bands sat 437px apart with 150px prints
       * between them; a 390×844 phone showed three 72px prints adrift in an
       * empty screen. Size and position must share a basis, and the band is
       * then CENTRED in whatever height the viewport actually has.
       * On a normal desktop window `vBasis === H` and `vTop === 0`, so every
       * number tuned on 16:10 and 16:9 is untouched. */
      vBasis = mobile ? H : Math.min(H, stage * 0.66);
      vTop = (H - vBasis) / 2;
      tailAt = mobile ? TAIL_NARROW : TAIL_AT;
      stage = Math.min(W, H * 1.85);
      stageX = (W - stage) / 2;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      maxCam = STOPS[STOPS.length - 1] * stage;
      camTarget = clamp(camTarget, 0, maxCam);

      typeCv!.width = Math.round(W * dpr);
      typeCv!.height = Math.round(H * dpr);
      typeCv!.style.width = `${W}px`;
      typeCv!.style.height = `${H}px`;
      tctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

      buildGrain();

      for (const p of plates) {
        const d = p.def;
        p.live = !mobile || !!d.m;
        if (!p.live) {
          p.el.style.display = "none";
          continue;
        }
        p.el.style.display = "";
        const m = mobile ? d.m! : d;
        /* Print heights are a fraction of a CLAMPED basis, not of H. Left on
         * raw H, a print's width in stage units tracks H/stage — which swings
         * from 0.54 on a wide window to 0.83 on a tall one, a 50% spread. A
         * run laid out to clear on 16:10 then collides on a tall window, and no
         * amount of gap fixes it because the collision scales with the viewport.
         * Clamping makes every width a fixed fraction of the stage, so ONE
         * layout is provably clear everywhere — and it stops prints ballooning
         * to fill a tall screen, which they should never do anyway. */
        const imgH = m.h * vBasis;
        const imgW = imgH * d.ar;
        const pad = Math.round(imgW * 0.045); // an even mat — no polaroid chin
        p.fw = Math.round(imgW + pad * 2);
        p.fh = Math.round(imgH + pad * 2);
        p.pinPx = Math.round(p.fw * d.pin);
        const pinAt = (fx: number, fy: number) => [
          Math.round(stageX + fx * stage - p.fw / 2),
          /* never let a frame hang off the bottom of the wall */
          Math.round(clamp(vTop + fy * vBasis, 0.03 * H, H * 0.965 - p.fh)),
        ];
        [p.homeX, p.homeY] = pinAt(m.x, m.y);
        const nx = mobile ? d.m?.n : d.n;
        if (nx) {
          /* the shrink is a SCALE about the pin, not a resize — a resize would
           * be a layout pass per frame, and the print would stop hanging from
           * the same point. Scaling about the pin keeps the nail still while
           * the paper gets smaller, which is the only version that reads. */
          p.nScale = nx.h ? nx.h / m.h : 1;
          const sc = p.nScale;
          p.homeX2 = Math.round(
            stageX + nx.x * stage - p.pinPx * (1 - sc) - (p.fw * sc) / 2
          );
          p.homeY2 = Math.round(clamp(nx.y * H, 0.03 * H, H * 0.965 - p.fh * sc));

          /* ── THE MIGRATION WINDOW IS SOLVED, NOT AUTHORED ──
           *
           * A migrating print is a blend between two parallax lines a CONSTANT
           * `D` apart, so its on-screen speed is
           *
           *     dx/dcam = −r + D·f′(u)/G       D = G·r − Δ
           *
           * with `G` the beat in px, `Δ` how far left it must travel, and `f′`
           * the blend's slope — `1/nSpan` inside the window. Compressing the
           * window raises `f′`, and past `nSpan = D/(G·r)` the second term wins
           * and THE PRINT TRAVELS RIGHT: it sails in from the right, stops
           * dead, then creeps backwards for the rest of the beat while its
           * neighbours keep going. Three prints, two directions.
           *
           * The threshold is not a constant — it is `1 − Δ/(G·r)`, which rises
           * toward 1 as the beat lengthens. `nSpan: 0.85` was correct for a
           * 0.35-stage opening and silently wrong the moment that beat went to
           * 0.80: the minimums became 0.897 / 0.849 / 0.798 and the first two
           * prints inverted. Authored windows cannot survive a change to the
           * thing they were solved against, so they are no longer authored.
           *
           * `m` is the margin — the print must still be visibly LEFT-bound, not
           * merely not-right-bound, or it reads as stuck. */
          const G = stopPx(d.nAt ?? d.ch + 1) - stopPx(d.ch);
          if (G > 0) {
            const m = 0.06;
            const q = (G * p.rate - (p.homeX - p.homeX2)) / (p.rate * G);
            p.spanV = clamp(d.nSpan ?? 1, Math.min(1, q / (1 - m)), 1);
            p.lagV = clamp(d.nLag ?? 0, 0, 1 - p.spanV);
          } else {
            p.spanV = d.nSpan ?? 1;
            p.lagV = d.nLag ?? 0;
          }
        } else {
          p.homeX2 = null;
          p.homeY2 = p.homeY;
          p.nScale = 1;
          p.spanV = 1;
          p.lagV = 0;
        }
        /* scale about the pin, so hover-lift and the re-hang shrink agree */
        p.inner.style.transformOrigin = `${p.pinPx}px 0px`;

        p.card.style.width = `${p.fw}px`;
        p.card.style.height = `${p.fh}px`;
        p.card.style.padding = `${pad}px`;
        p.el.style.transformOrigin = `${p.pinPx}px 0px`;
        /* the tail-relative schedule, resolved once per layout rather than
         * rebuilt per frame */
        if (d.tailPush !== undefined) {
          p.holdAtV = tailAt + d.tailPush;
          p.driftV = { to: tailAt + (d.tailExit ?? 0), rate: PUSH_RATE };
        } else {
          p.holdAtV = d.holdAt;
          p.driftV = d.drift;
        }
        const win = p.card.firstElementChild as HTMLElement;
        win.style.height = `${Math.round(imgH)}px`;
        const capEl = p.card.querySelector<HTMLElement>("[data-chin]")!;
        capEl.style.fontSize = `${clamp(p.fw * 0.034, 6.5, 10.5)}px`;
      }

      measureType();

      for (const b of blocks) {
        b.cx = mobile ? b.xm : b.x;
        b.cy = mobile ? b.ym : b.y;
        b.el.style.width = `${Math.round((mobile ? b.wm : b.w) * stage)}px`;
        if (b.holdTail !== null) b.holdAt = tailAt + b.holdTail;
        /* THE CONTACT BOARD IS CENTRED, NOT PLACED. It is the destination —
         * the one block that is a composition in its own right rather than a
         * caption hanging off the wall — so equal air above and below is the
         * whole of its layout. A fixed `y` cannot deliver that at any
         * viewport: the panel is `min-height: 66vh` and the footer wraps, so
         * the block's own height moves with the window. At `y 0.13` it
         * measured 126 above and 208 below. Read the height and solve for the
         * top edge instead — one forced reflow per layout, never per frame. */
        if (b.ch === STOPS.length - 1) {
          b.cy = clamp((H - b.el.offsetHeight) / (2 * H), 0.02, 0.5);
        } else {
          /* every other block rides the same band as the prints and the type */
          b.cy = (vTop + b.cy * vBasis) / H;
        }
      }

      /* Hang every print BEFORE the browser can paint one. `layout()` runs from
       * an effect — after React has already committed the plates — so without
       * this pass there is a window between the commit and the first rAF where
       * nine prints sit un-transformed in the top-left corner. The load fade
       * happens to cover it today; nothing should depend on that, and a resize
       * has no fade to hide behind at all. */
      for (const p of plates) {
        if (!p.live) continue;
        const [sx, sy, ns] = place(p);
        p.el.style.transform = `translate3d(${sx}px, ${sy}px, 0) rotate(${p.ang}rad)`;
        p.inner.style.transform = `translate3d(0, 0, 0) scale(${ns})`;
      }
    }

    /* Headline metrics, split out of `layout()` on purpose. A font swap changes
     * NOTHING but glyph advances — not the viewport, not a print, not a copy
     * column — and re-running the whole layout for it re-assigns both canvas
     * bitmaps and rewrites the box styles of all nine cards in one frame. Do
     * that in the frame the entrance starts and the compositor presents before
     * it has re-rastered the plate layers: the photographs vanish for exactly
     * one frame and come back, which is the second half of "the images jump
     * twice". Re-measure the type and touch nothing else. */
    function measureType() {
      const remeasure = (wd: Wd) => {
        tctx!.font = `${wd.size}px ${SERIF}`;
        let off = 0;
        const glyphs: G[] = [];
        for (const ch of Array.from(wd.def.text)) {
          const w = tctx!.measureText(ch).width;
          glyphs.push({ ch, w, off });
          off += w;
        }
        wd.glyphs = glyphs;
      };
      for (const wd of words) {
        wd.size = Math.round(wd.def.size * vBasis * (mobile ? 0.56 : 1));
        wd.sizeN = Math.round((wd.def.nSize ?? wd.def.size) * vBasis * (mobile ? 0.56 : 1));
        wd.base = vTop + wd.def.base * vBasis;
        wd.baseN = vTop + (wd.def.nBase ?? wd.def.base) * vBasis;
        wd.homeX = stageX + wd.def.x * stage;
        wd.homeXN =
          wd.def.nX === undefined ? null : stageX + wd.def.nX * stage;
        remeasure(wd);
      }

      /* ── TWO GIANT WORDS ONLY FIT IF THE TYPE IS SOLVED AGAINST THE STAGE ──
       *
       * The words are sized off `vBasis`, which is a HEIGHT, but whether two of
       * them can share the wall is a WIDTH question. On a tall narrow screen
       * those come apart: `photographer` measures 1.36 stage on a desktop and
       * 2.63 on a phone — the same type, nearly twice the wall — and since both
       * words travel at one rate their separation is a CONSTANT, so once it is
       * negative they are welded together for the entire beat. Measured before
       * this: 437px of permanent overlap at 390 wide, 482px at 698, at every
       * camera position. The `0.56` narrow factor was not nearly enough, and no
       * single factor could be: the ratio depends on the viewport's shape.
       *
       * So the budget is derived from the wall itself. The incoming word rests
       * at `x`, and over the beat between the outgoing word's release and its
       * own stop it closes `rate × beat` of stage, so
       *
       *     widest allowed = inc.x + rate·(S_inc − S_out) − WORD_GAP
       *
       * which is the file's own separation rule solved for the width instead of
       * for the beat. Every word is then scaled by ONE factor, so their
       * relative sizes — the whole point of `photographer` dwarfing `work` — are
       * untouched, and on a desktop the budget (1.39) is already above what the
       * type measures (1.36), so nothing scales and nothing changes.
       *
       * IT TRACKS THE STOPS. Shorten `S3 − S2` and the type gets smaller rather
       * than colliding; lengthen it and the type is free to grow back. */
      if (words.length >= 2) {
        const out = words[0];
        const inc = words[words.length - 1];
        const beat =
          (stopPx(inc.def.ch) - stopPx(out.def.holdTo ?? out.def.ch + 1)) / stage;
        const budget = inc.def.x + out.rate * beat - WORD_GAP;
        let worst = 0;
        for (const wd of words) {
          const adv = wd.glyphs.reduce((s, g) => s + g.w, 0) * (wd.sizeN / wd.size);
          worst = Math.max(worst, adv / stage);
        }
        if (budget > 0 && worst > budget) {
          const k = budget / worst;
          for (const wd of words) {
            wd.size = Math.max(8, Math.round(wd.size * k));
            wd.sizeN = Math.max(8, Math.round(wd.sizeN * k));
            remeasure(wd);
          }
        }
      }
    }

    /* ── film grain: one noise tile, blown up once, then jittered forever ── */
    function buildGrain() {
      const gw = Math.ceil(W) + 48;
      const gh = Math.ceil(H) + 48;
      grainCv!.width = gw;
      grainCv!.height = gh;
      grainCv!.style.width = `${gw}px`;
      grainCv!.style.height = `${gh}px`;
      const tile = document.createElement("canvas");
      tile.width = 256;
      tile.height = 256;
      const tc = tile.getContext("2d")!;
      const id = tc.createImageData(256, 256);
      for (let i = 0; i < id.data.length; i += 4) {
        const v = 128 + (Math.random() - 0.5) * 255;
        id.data[i] = id.data[i + 1] = id.data[i + 2] = v;
        id.data[i + 3] = 255;
      }
      tc.putImageData(id, 0, 0);
      gctx!.clearRect(0, 0, gw, gh);
      const pat = gctx!.createPattern(tile, "repeat");
      if (pat) {
        gctx!.fillStyle = pat;
        gctx!.fillRect(0, 0, gw, gh);
      }
    }

    /* ── input ── */
    /* SCROLL and EASE live in DEFAULT_TUNE with the rest of the knobs; they are
     * destructured at the top of this effect, so everything below reads exactly
     * as it did when they were declared here. */
    let dragPlate: P | null = null;
    let touchIgnore = false;
    let touchY = 0;
    /* ── TOUCH HAS ONE GESTURE FOR TWO JOBS ──
     *
     * On a desktop the two never meet: the wheel pans and a drag swings a
     * print. On a phone both are a finger on the glass, and the print wins
     * because its `pointerdown` sets `dragPlate` before the wall ever sees a
     * `touchmove`. Prints cover 28% of a phone screen at the opening, so better
     * than a quarter of all swipes did nothing but rock a photograph while the
     * wall sat still — which does not read as a feature, it reads as a section
     * that does not scroll.
     *
     * So a touch does not commit to anything until it has shown which gesture
     * it is. Below the slop, neither happens; past it, a mostly-SIDEWAYS drag
     * swings the print (you push a hanging thing sideways) and anything else is
     * the wall. The bias is deliberate: panning is the gesture someone needs,
     * swinging is the one they discover, so the ambiguous middle goes to the
     * wall. A mouse still grabs instantly — nothing about the desktop feel
     * changes. */
    const TOUCH_SLOP = 6; //   px before a touch means anything
    const SWING_BIAS = 1.2; // how much more sideways than vertical to swing
    let pendPlate: P | null = null;
    let pendX = 0;
    let pendY = 0;
    let touchX = 0;
    let mx = -9999;
    let my = -9999;
    let mSpeed = 0;
    let inside = false;

    const push = (dy: number) => {
      camTarget = clamp(camTarget + dy * SCROLL, 0, maxCam);
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault(); // this gesture pans the wall, not the page
      push(e.deltaY);
    };
    const onTouchStart = (e: TouchEvent) => {
      const t = e.target as HTMLElement | null;
      touchIgnore = !!t?.closest("[data-block='contact'] input, [data-block='contact'] textarea, [data-block='contact'] button");
      touchY = e.touches[0]?.clientY ?? 0;
      touchX = e.touches[0]?.clientX ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (touchIgnore || dragPlate) return;
      const y = e.touches[0]?.clientY ?? touchY;
      const x = e.touches[0]?.clientX ?? touchX;
      /* a touch that started on a print is still undecided — resolve it here,
       * on the first movement big enough to have a direction */
      if (pendPlate) {
        const dx = Math.abs(x - pendX);
        const dy = Math.abs(y - pendY);
        if (dx < TOUCH_SLOP && dy < TOUCH_SLOP) return; // no verdict yet
        if (dx > dy * SWING_BIAS) {
          const r = root!.getBoundingClientRect();
          const p = pendPlate;
          dragPlate = p;
          p.grab = true;
          p.grabOff = p.ang - angleTo(p, pendX - r.left, pendY - r.top);
          pendPlate = null;
          return; //                       ... it was a swing after all
        }
        pendPlate = null; //               it is the wall; pan from here
        touchY = y;
        touchX = x;
        return;
      }
      push(touchY - y);
      touchY = y;
      touchX = x;
      if (e.cancelable) e.preventDefault();
    };
    /* A touch-activated swing never took pointer capture — it could not, the
     * grab is decided a frame after the pointer went down. So the plate's own
     * `pointerup` is not guaranteed to fire (a finger that drifts off it lifts
     * somewhere else), and without this the wall would stay locked to a print
     * the visitor already let go of. The wall is the thing that must never get
     * stuck, so its release is owned here rather than by the plate. */
    const onTouchEnd = () => {
      pendPlate = null;
      if (dragPlate) {
        dragPlate.grab = false;
        dragPlate = null;
      }
    };
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA)$/.test(t.tagName)) return;
      if (e.key === "ArrowRight" || e.key === "PageDown") { jump(nearest() + 1); e.preventDefault(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { jump(nearest() - 1); e.preventDefault(); }
      else if (e.key === "Home") { jump(0); e.preventDefault(); }
      else if (e.key === "End") { jump(STOPS.length - 1); e.preventDefault(); }
    };

    const nearest = () => {
      let best = 0;
      let bd = Infinity;
      for (let i = 0; i < STOPS.length; i++) {
        const d = Math.abs(cam - stopPx(i));
        if (d < bd) { bd = d; best = i; }
      }
      return best;
    };
    const jump = (ch: number) => {
      camTarget = clamp(stopPx(clamp(ch, 0, STOPS.length - 1)), 0, maxCam);
    };
    gotoRef.current = jump;

    const onMove = (e: PointerEvent) => {
      const r = root!.getBoundingClientRect();
      const nx = e.clientX - r.left;
      const ny = e.clientY - r.top;
      if (mx > -9000) mSpeed = Math.min(70, Math.hypot(nx - mx, ny - my));
      mx = nx;
      my = ny;
      inside = true;
    };
    const onLeave = () => { inside = false; mx = my = -9999; mSpeed = 0; };

    /* Where a print is right now. A print with a second pin doesn't slide away
     * with the wall — it TRAVELS to its new spot across the gap between its own
     * stop and the next, so the scene visibly re-arranges itself. Both anchors
     * use the same parallax formula, blended; at either end it lands exact. */
    /* A hold freezes the CLOCK an element reads, from the moment it finishes
     * its migration until `holdTo`. Everything downstream — parallax, blends,
     * derivatives — is then unchanged; the element simply stops experiencing
     * time for a stretch and resumes exactly where it left off. */
    /* A HOLD IS A CLOCK RUNNING AT 0×, AND A DRIFT IS THE SAME CLOCK RUNNING
     * SLOW. Once that is the model, `drift` costs one branch: between `bq` and
     * `drift.to` the element experiences `drift.rate` of every pixel of camera,
     * so it travels at `rate × drift.rate` on screen and everything downstream
     * — parallax, blends, derivatives — is untouched, exactly as with a hold. */
    const heldCam = (
      ch: number,
      nAt: number | undefined,
      holdTo: number | undefined,
      holdAt?: number,
      drift?: { to: number; rate?: number; ease?: boolean },
      holdRate?: number
    ) => {
      if (holdTo === undefined && holdAt === undefined) return cam;
      const a = stopPx(nAt ?? ch + 1);
      const bq = holdAt !== undefined ? holdAt * stage : stopPx(holdTo!);
      const hr = holdRate ?? 0;
      if (cam <= a) return cam;
      if (cam <= bq) return a + (cam - a) * hr; // held — 0 is a dead freeze
      const base = a + (bq - a) * hr; //      where the held phase left off
      if (!drift) return base + (cam - bq); // straight back to full speed
      const d = drift.to * stage;
      /* EASED: the clock climbs back to full rate at the far edge. It is the
       * arrival curve run backwards, so a print leaves its pin the way it
       * landed on it, and because the clock has fully caught up by `to` the
       * exit is not delayed. A constant fraction only moves the discontinuity:
       * measured, the wall still stepped −30 → −86 px per notch at the end.
       *
       * IT MUST START AT THE RATE THE ELEMENT IS ALREADY MOVING AT, not at
       * zero. The held formation is drifting (see HOLD_DRIFT) when the release
       * arrives, so the plain `2u² − u³` — whose slope is zero at u = 0 —
       * brought it to a DEAD STOP for a frame before accelerating away. The
       * cubic through `g(0)=0, g'(0)=k, g(1)=1, g'(1)=1` is the same curve
       * generalised, and collapses to `2u² − u³` at k = 0. */
      if (drift.ease) {
        if (cam >= d) return base + (d - bq) + (cam - d);
        const u = (cam - bq) / (d - bq);
        const k = drift.rate ?? 0;
        return base + (d - bq) * ((k - 1) * u * u * u + 2 * (1 - k) * u * u + k * u);
      }
      const r = drift.rate ?? 1;
      if (cam <= d) return base + (cam - bq) * r; //  creeping
      return base + (d - bq) * r + (cam - d); //      full speed again
    };

    const place = (p: P) => {
      const cam = heldCam(p.def.ch, p.def.nAt, p.def.holdTo, p.holdAtV, p.driftV, p.def.holdRate);
      const st = stopPx(p.def.ch) + (p.def.aLead ?? 0) * stage;
      /* ── approach ── still travelling toward its own stop: its own speed, its
       * own landing moment, and a decelerating finish. Outside the window it is
       * plain parallax, so the two meet at matched speed. */
      if (cam < st && (p.def.aRate || p.def.aEase || p.def.aLead)) {
        const r = p.def.aRate ?? p.rate;
        const W = (p.def.aEase ?? 0) * stage;
        const gap = st - cam;
        const u2 = W > 0 ? gap / W : 2;
        const off = u2 >= 1 ? gap * r : W * r * (2 * u2 * u2 - u2 * u2 * u2);
        return [p.homeX + off, p.homeY, 1] as const;
      }
      const sxA = p.homeX - (cam - st) * p.rate;
      if (p.homeX2 === null) return [sxA, p.homeY, 1] as const;
      const next = stopPx(p.def.nAt ?? p.def.ch + 1);
      /* `u` is the raw position through the span; `raw` is this print's own
       * window inside it. Lag and span are what give three prints three paces:
       * before its window opens a print runs at pure parallax, then it eases
       * onto its second anchor. NOTE a pinned print (n.x === x) must keep the
       * default window — the two anchors only cancel when raw === u. */
      const u = clamp01((cam - st) / (next - st));
      const raw = clamp01((u - p.lagV) / p.spanV);
      const t = smooth(raw);
      const sxB = p.homeX2 - (cam - next) * p.rate;
      /* HORIZONTAL blend is linear — same reason the headline's is. The second
       * anchor pulls RIGHT relative to pure parallax, so an eased blend hands
       * the opening frames entirely to parallax and then whips back: the print
       * jerks instead of gliding. Linear keeps dx/dcam constant and negative
       * (≈ −0.45 here), so every print moves LEFT on every frame of the beat.
       * Vertical and scale keep the ease — nothing pulls against those. */
      return [
        lerp(sxA, sxB, raw),
        lerp(p.homeY, p.homeY2, t),
        lerp(1, p.nScale, t),
      ] as const;
    };
    /* grabbing a print: the angle tracks the pointer around the pin */
    const pinScreen = (p: P) => {
      const [sx, sy] = place(p);
      return [sx + p.pinPx, sy] as const;
    };
    const angleTo = (p: P, px: number, py: number) => {
      const [ax, ay] = pinScreen(p);
      return Math.atan2(ax - px, Math.max(12, py - ay));
    };

    const cleanups: Array<() => void> = [];
    for (const p of plates) {
      const enter = () => { p.hoverT = 1; };
      const leave = () => { p.hoverT = 0; };
      const down = (e: PointerEvent) => {
        if (reduce) return;
        /* A FINGER IS NOT YET A GRAB. Committing here is what left the wall
         * unable to pan from 28% of its own surface; the verdict is deferred to
         * `onTouchMove`, which is the first place the gesture has a direction.
         * Note the missing `preventDefault` and pointer capture — taking either
         * now would swallow the pan this touch may turn out to be. */
        if (e.pointerType === "touch") {
          pendPlate = p;
          pendX = e.clientX;
          pendY = e.clientY;
          return;
        }
        e.preventDefault();
        p.el.setPointerCapture?.(e.pointerId);
        const r = root!.getBoundingClientRect();
        dragPlate = p;
        p.grab = true;
        p.grabOff = p.ang - angleTo(p, e.clientX - r.left, e.clientY - r.top);
      };
      const up = (e: PointerEvent) => {
        if (pendPlate === p) pendPlate = null; // a tap that never chose
        if (!p.grab) return;
        p.grab = false;
        dragPlate = null;
        p.el.releasePointerCapture?.(e.pointerId);
      };
      p.el.addEventListener("pointerenter", enter);
      p.el.addEventListener("pointerleave", leave);
      p.el.addEventListener("pointerdown", down);
      p.el.addEventListener("pointerup", up);
      p.el.addEventListener("pointercancel", up);
      cleanups.push(() => {
        p.el.removeEventListener("pointerenter", enter);
        p.el.removeEventListener("pointerleave", leave);
        p.el.removeEventListener("pointerdown", down);
        p.el.removeEventListener("pointerup", up);
        p.el.removeEventListener("pointercancel", up);
      });
    }

    /* ── entrance ── */
    /* the load-in clock: a linear 0→1 that gates the copy reveal and the
     * chrome fade. The photographs do NOT read it — see below. */
    const intro = { v: reduce ? 1 : 0 };
    /* written once per frame while the fade runs, then cleared and never
     * touched again — a settled wall costs nothing, and after this the prints
     * carry no inline opacity at all, so scrolling one into view cannot fade
     * it. The fade belongs to the PAGE LOAD, not to the print. */
    /* NOT seeded from `reduce`: the prints are hidden by CSS until the root
     * drops `data-intro`, so something has to run and clear it. Let the first
     * frame run instead — under reduced motion the fade is already complete
     * there, so it clears in one pass with no fade. */
    let introFaded = false;
    let handedOver = false;
    layout();

    /* GSAP owns the entrance only: the headline rises out of blur, the chrome
     * staggers in, the opening lines lift out of their masks. It is created on
     * the readiness gate below rather than at mount — a `fromTo` applies its
     * FROM state the moment it exists, so building it early would fight the
     * CSS that is holding the headline hidden. */
    let disposed = false;
    let tl: gsap.core.Timeline | null = null;
    const startEntrance = () => {
      tl = gsap.timeline();
      if (reduce) return;
      tl.to(intro, { v: 1, duration: 1.9, ease: "none" }, 0.1);
      tl.fromTo(
        typeCv,
        { opacity: 0, filter: "blur(14px)", scale: 1.04 },
        { opacity: 1, filter: "blur(0px)", scale: 1, duration: 1.6, ease: "power4.out" },
        0
      );
      tl.fromTo(
        root.querySelectorAll("[data-chrome]"),
        { opacity: 0, y: -8 },
        { opacity: 1, y: 0, duration: 0.9, stagger: 0.06, ease: "power3.out" },
        0.5
      );
    };

    /* THE FADE STARTS ON DECODE, NOT ON A CLOCK.
     * A timed fade is fine on a warm cache and wrong on the load that matters:
     * cold, the mat fades up EMPTY and the picture lands inside it a beat
     * later — and a progressive JPEG lands twice, once coarse and once sharp.
     * Either way the print visibly changes after you can already see it, which
     * is the little jump on first load. `decode()` is the one API that answers
     * "is this paintable"; waiting on it means a photograph is whole before it
     * is visible at all, so nothing about it moves once it is.
     * Only the OPENING TRIO is waited on — the rest of the wall is off-frame
     * right with seconds of panning to decode in.
     * The cap catches a FAILED request, not a slow one. Tuned down to ~2s it
     * fires on any bad connection and hands back the exact pop it exists to
     * prevent (measured: 30 frames of visible-but-undecoded film at 400kbps).
     * 8s is past every real load — 236KB is under 200ms on anything normal —
     * so it only ever runs when the network has genuinely died. Waiting is the
     * right answer meanwhile: the headline and the copy still play, and a wall
     * whose photographs arrive late is honest in a way that a wall of empty
     * mats filling in one by one is not. */
    /* Reduced motion waits too — it asks for no ANIMATION, not for a picture
     * that changes after you can see it. It simply skips the fade below. */
    let fadeFrom = -1; // ms stamp; −1 = still waiting on the film
    /* THE HEADLINE WAITS FOR ITS TYPEFACE, for exactly the same reason.
     * A canvas headline is MEASURED, not laid out — `measureText` against
     * whatever face is resolved at that instant. Draw it before Playfair has
     * arrived and every glyph advance is Georgia's, so when `fonts.ready` fires
     * and re-measures, the word snaps to a new width and baseline (measured:
     * 96px wider, 8px higher) with one blank frame in between, because
     * re-assigning `canvas.width` clears it and the redraw is a frame later.
     * That lands on top of the photographs fading up and reads as the images
     * jumping — which is what it was mistaken for. A measured element must not
     * be visible until the thing it measures against exists. */
    const entrance = (() => {
      const openers = plates.filter((p) => p.live && p.def.ch === 0);
      const film = openers.map((p) => {
        const img = p.card.querySelector("img");
        return img?.decode ? img.decode().catch(() => {}) : Promise.resolve();
      });
      const face = document.fonts?.ready ?? Promise.resolve();
      const cap = new Promise((r) => setTimeout(r, 8000));
      return Promise.race([Promise.all([...film, face]), cap]);
    })();
    entrance.then(() => {
      if (disposed) return;
      /* re-measure with the real face while everything is still hidden */
      measureType();
      if (fadeFrom < 0) fadeFrom = performance.now();
      startEntrance();
    });

    let paused = false;
    let raf = 0;
    let lastChapter = -1;

    /* ── the loop ── */
    const frame = () => {
      raf = requestAnimationFrame(frame);
      if (paused) return;
      tick++;

      /* camera — one short smoothing lerp and nothing else. No magnet, no
       * momentum: where you stop scrolling is where the wall stops. */
      camPrev = cam;
      cam += (camTarget - cam) * EASE;
      vel = cam - camPrev;

      const ch = nearest();
      if (ch !== lastChapter) { lastChapter = ch; setChapter(ch); }

      /* ── headline ribbon ── */
      tctx!.clearRect(0, 0, W, H);
      tctx!.fillStyle = palRef.current.accent;
      for (const wd of words) {
        /* ONE headline at a time. A word owns its own stretch of wall and is
         * GONE — not faint — before the next one starts to arrive.
         *
         * The falloff has to complete inside HALF the distance between two
         * words' stretches (0.72·stage apart, so under 0.36), or the midpoint
         * leaves both of them sitting at the same alpha and you get two giant
         * words abreast. At 0.08 + 0.26 each is fully out by 0.34·stage, which
         * buys a hair of empty wall between them rather than an overlap.
         * smootherstep keeps that short window from reading as a hard cut. */
        const st = stopPx(wd.def.ch);
        const a0 = stopPx(wd.def.ch - (wd.def.pre || 0));
        const a1 = stopPx(wd.def.ch + (wd.def.hold || 0));
        const gap = cam < a0 ? a0 - cam : cam > a1 ? cam - a1 : 0;
        const alpha =
          0.94 * (1 - smooth(clamp01((gap - 1.1 * stage) / (0.5 * stage))));
        if (alpha < 0.012) continue;
        tctx!.globalAlpha = alpha;
        /* the intro word starts on the floor and, over the re-arrangement beat,
         * climbs to the middle, walks RIGHT (against a camera panning left) and
         * SHRINKS — the same recession the prints make, so the opening settles
         * back as one scene rather than three objects doing separate things. */
        const next = stopPx(wd.def.nAt ?? wd.def.ch + 1);
        /* position reads the HELD clock; alpha above deliberately reads the
         * real one, so freezing a word never changes when it is legible */
        const camW = heldCam(wd.def.ch, wd.def.nAt, wd.def.holdTo, undefined, wd.def.drift, wd.def.holdRate);
        const raw = clamp01((camW - st) / (next - st));
        const tn = smooth(raw);
        /* The horizontal blend is LINEAR, and it has to be. The word's anchor
         * travels RIGHT (x → nX) while parallax drags it LEFT at `rate`; the
         * two only add up to a rightward move if the anchor advances at a
         * steady clip. Ease the blend and smootherstep's flat toe hands the
         * first frames entirely to parallax — the word sets off LEFT, dips a
         * third of a stage, then swings back right past its target and settles.
         * Formally: wx' = (Δanchor/gap)·f' − rate, so any f with f'(0) = 0
         * starts negative no matter how the numbers are tuned. Only the
         * vertical climb and the size change — pure lerps with nothing pulling
         * against them — get the eased scalar. */
        const tnX = raw;
        const size = wd.sizeN === wd.size ? wd.size : lerp(wd.size, wd.sizeN, tn);
        /* advances are linear in size, so the glyph run measured once at the
         * base size just scales — no re-measuring per frame */
        const k = size / wd.size;
        tctx!.font = `${size}px ${SERIF}`;
        const wx0 = wd.homeX - (camW - st) * wd.rate;
        const wx =
          wd.homeXN === null
            ? wx0
            : lerp(wx0, wd.homeXN - (camW - next) * wd.rate, tnX);
        const baseY = wd.baseN === wd.base ? wd.base : lerp(wd.base, wd.baseN, tn);
        /* The word is drawn as a RIGID run: every glyph at its exact advance,
         * on one baseline. It used to carry two scroll-driven per-glyph
         * effects — a spring lag that stretched the word while the camera moved
         * and a sine bob that rippled the baseline — and together they read as
         * the type wobbling every time you scrolled. At this size a wave is not
         * a texture, it is a wobble: the letters are half a viewport tall, so a
         * 1% vertical offset is a visible step between neighbours. Type this
         * big has to sit still and let the WALL do the moving. */
        for (let i = 0; i < wd.glyphs.length; i++) {
          const g = wd.glyphs[i];
          const gx = wx + g.off * k;
          if (gx > -size * 2 && gx < W + size) tctx!.fillText(g.ch, gx, baseY);
        }
      }
      tctx!.globalAlpha = 1;

      /* ── the prints ── */
      if (!introFaded) {
        /* the whole wall fades up together over ~0.95s once the film is ready.
         * Together, not one print at a time: nine separate decodes finishing
         * at nine different moments is exactly the stutter this replaces. */
        const o =
          fadeFrom < 0
            ? 0
            : reduce
              ? 1
              : smooth(clamp01((performance.now() - fadeFrom) / 950));
        /* HAND THE GATE OVER AT THE START OF THE FADE, NOT THE END.
         * By this frame every gated element already carries an inline opacity
         * of its own — the plates from the line below, the headline and chrome
         * from the timeline `startEntrance` just built — so dropping the
         * attribute changes nothing that is on screen. */
        if (fadeFrom >= 0 && !handedOver) {
          handedOver = true;
          setIntroDone(true);
        }
        /* AND DO NOT CLEAR THE INLINE OPACITY UNTIL THE ATTRIBUTE IS ACTUALLY
         * GONE. `setIntroDone` is a state update: React commits it a frame or
         * more later, and until then the stylesheet is still the only rule in
         * play. Clear the inline value on the strength of having *called* the
         * setter and the entire wall blinks out for exactly one painted frame —
         * invisible to `getComputedStyle` (React has committed by the time you
         * can sample it) and unmistakable in a compositor capture, where the
         * prints drop to zero for one frame right after the fade completes.
         * That is the second of the two jumps. Read the DOM, not the state. */
        if (o >= 1 && !root.hasAttribute("data-intro")) {
          introFaded = true;
          for (const p of plates) p.el.style.opacity = "";
        } else {
          const os = String(o);
          for (const p of plates) p.el.style.opacity = os;
        }
      }
      for (const p of plates) {
        if (!p.live) continue;
        const d = p.def;
        const [sx, sy, ns] = place(p);
        const onScreen = sx + p.fw > -0.08 * W && sx < 1.08 * W;

        /* pendulum — two substeps keep a hard fling stable */
        /* a print panning under a parked cursor shouldn't lift by itself —
         * hover only answers while the wall is at rest */
        const wantsHover = p.hoverT && Math.abs(vel) < 0.6 ? 1 : 0;
        p.hover += (wantsHover - p.hover) * 0.14;
        if (!reduce) {
          if (p.grab) {
            const t = clamp(angleTo(p, mx, my) + p.grabOff, p.base - 1.1, p.base + 1.1);
            const next = lerp(p.ang, t, 0.42);
            p.angVel = next - p.ang;
            p.ang = next;
          } else {
            /* a cursor sweeping past shoves the print away from itself */
            if (inside && onScreen && mSpeed > 0.6) {
              const dx = p.cx - mx;
              const dy = p.cy - my;
              const R = p.fw * 1.6;
              const dist = Math.hypot(dx, dy);
              if (dist < R) {
                const f = 1 - dist / R;
                p.angVel -= Math.sign(dx) * f * f * mSpeed * K_CURSOR;
              }
            }
            /* the pendulum only runs when something has actually disturbed
             * it — the cursor, your hand, or the entrance. Panning does not. */
            if (p.angVel !== 0 || p.ang !== p.base) {
              for (let s = 0; s < 2; s++) {
                const t =
                  (-K_SPRING * Math.sin(p.ang - p.base)) / d.len -
                  K_DAMP * p.angVel;
                p.angVel += t * 0.5;
                p.ang += p.angVel * 0.5;
              }
              if (Math.abs(p.angVel) < 1e-4 && Math.abs(p.ang - p.base) < 1e-4) {
                p.angVel = 0; // snap to rest rather than integrate forever
                p.ang = p.base;
              }
            }
            /* a hovered print is steadied, as if you'd put a finger on it */
            if (p.hover > 0.01) {
              p.angVel *= 1 - 0.09 * p.hover;
              p.ang += (p.base - p.ang) * 0.035 * p.hover;
            }
            p.ang = clamp(p.ang, p.base - MAX_SWING, p.base + MAX_SWING);
          }
        }

        p.el.style.transform = `translate3d(${sx}px, ${sy}px, 0) rotate(${p.ang}rad)`;
        p.inner.style.transform = `translate3d(0, ${-15 * p.hover}px, 0) scale(${(1 + 0.045 * p.hover) * ns})`;

        /* shadow swaps in coarse steps — it's a repaint, not a composite */
        const key = p.hover > 0.5 ? 1 : 0;
        if (key !== p.shadowKey) {
          p.shadowKey = key;
          p.card.style.boxShadow = key
            ? palRef.current.shadowNear
            : d.d > 0.4
              ? palRef.current.shadowFar
              : palRef.current.shadowNear;
        }
        /* prints stack in the order they were pinned up; a hovered one comes
         * to the front. The odd numbers between them belong to the copy, so a
         * new chapter's prints pin OVER its text while older ones slide behind */
        p.el.style.zIndex = String(20 + p.i * 2 + (p.hover > 0.02 ? 80 : 0));

        /* cache the centre for next frame's proximity test */
        const cxLocal = (p.fw / 2 - p.pinPx) * ns;
        const cyLocal = (p.fh / 2) * ns;
        const c = Math.cos(p.ang);
        const s2 = Math.sin(p.ang);
        p.cx = sx + p.pinPx + cxLocal * c - cyLocal * s2;
        p.cy = sy + cxLocal * s2 + cyLocal * c;
      }

      /* ── copy blocks + the contact card ── */
      for (const b of blocks) {
        const st = stopPx(b.ch);
        /* ONE line of copy on screen at a time. The window opens at
         * `st − 0.24·stage`, by which point the previous block — the widest is
         * 0.42·stage — has fully cleared the left edge, and closes just before
         * the stop. Open it any earlier (the obvious 0.66/0.44) and the next
         * paragraph starts fading up while the last one is still being read,
         * which is the single fastest way to make a scrolling narrative
         * feel like noise. */
        /* The contact board does NOT reveal. It is the destination, not a
         * caption — it should simply be there when you reach it, the way the
         * photographs are. Nothing hides it in the meantime because nothing
         * needs to: at `x 0.25` travelling at rate 1.4 it sits 5.5 stage off to
         * the right for the whole wall, so geometry keeps it away exactly as it
         * does for the prints. A reveal here only ever catches the panel
         * half-faded with its fields still climbing. */
        const isBoard = b.ch === STOPS.length - 1;
        const prog = isBoard
          ? 1
          : Math.min(
              smooth(intro.v),
              clamp01((cam - (st - b.lead * stage)) / (b.span * stage))
            );
        const from = b.holdAt
          ? Math.max(0, cam - b.holdAt * stage)
          : b.hold
            ? Math.max(0, cam - stopPx(b.ch + b.hold))
            : cam - st;
        const sx = stageX + b.cx * stage - from * b.rate;
        /* EXIT FADE. One paragraph at a time cannot be guaranteed by geometry
         * alone: a block clears the frame only after travelling its own width,
         * and on a phone the text column is wider than the whole first beat
         * provides. No reveal timing fixes that — the block is still physically
         * on screen. Fading it out on its way makes the rule hold at any width
         * and any viewport. Tuned to finish inside the SHORTEST beat minus the
         * next block's lead (0.35 − 0.09 = 0.26), so the handover never doubles
         * up. The contact board is exempt: it is the destination, not a caption. */
        const exiting = isBoard
          ? 1
          : 1 - smooth(clamp01((from - 0.15 * stage) / (0.1 * stage)));
        /* `lift` walks a block up from below as its beat lands */
        const sy = b.cy * H + (1 - smooth(prog)) * b.lift * H;
        b.el.style.transform = `translate3d(${Math.round(sx)}px, ${Math.round(sy)}px, 0)`;
        for (let i = 0; i < b.lines.length; i++) {
          const el = b.lines[i];
          const t = smooth(clamp01((prog - i * 0.11) / 0.5));
          el.style.transform = `translate3d(0, ${(1 - t) * 108}%, 0)`;
          el.style.opacity = String(t * exiting);
          /* The mask exists only to hide the line BEFORE it rises. Left clipping
           * once it has arrived, it also crops every descender — Playfair's
           * glyphs run below the line box, so `light` loses the tail of its g.
           * Open the mask the moment the line is home. */
          const ov = t > 0.999 ? "visible" : "hidden";
          if (el.dataset.ov !== ov) {
            el.dataset.ov = ov;
            (el.parentElement as HTMLElement).style.overflow = ov;
          }
        }
      }

      /* ── grain: a still grain is wallpaper; this one runs ── */
      if (tick % 2 === 0) {
        grainCv!.style.transform = `translate3d(${-24 + Math.random() * 22}px, ${-24 + Math.random() * 22}px, 0)`;
      }

      mSpeed *= 0.82;
    };

    /* keep the palette fresh inside the loop without restarting it */
    const palRef = { current: readTheme() === "dark" ? DARK : LIGHT };
    const themeObs = new MutationObserver(() => {
      palRef.current = readTheme() === "dark" ? DARK : LIGHT;
      for (const p of plates) p.shadowKey = -1;
    });
    themeObs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    /* ── wiring ── */
    root.addEventListener("wheel", onWheel, { passive: false });
    root.addEventListener("touchstart", onTouchStart, { passive: true });
    root.addEventListener("touchmove", onTouchMove, { passive: false });
    root.addEventListener("touchend", onTouchEnd, { passive: true });
    root.addEventListener("touchcancel", onTouchEnd, { passive: true });
    root.addEventListener("pointermove", onMove);
    root.addEventListener("pointerleave", onLeave);
    window.addEventListener("keydown", onKey);

    const ro = new ResizeObserver(() => layout());
    ro.observe(root);
    /* The entrance gate already re-measures once the face has loaded. This is
     * the backstop for the case where the 8s cap fired first: a permanently
     * mis-set headline is worse than one late re-measure. */
    document.fonts?.ready.then(() => measureType()).catch(() => {});

    const io = new IntersectionObserver(
      ([e]) => { paused = !e.isIntersecting; },
      { threshold: 0.01 }
    );
    io.observe(root);
    const onVis = () => { paused = document.hidden; };
    document.addEventListener("visibilitychange", onVis);

    raf = requestAnimationFrame(frame);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      tl?.kill();
      ro.disconnect();
      io.disconnect();
      themeObs.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      root.removeEventListener("wheel", onWheel);
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchmove", onTouchMove);
      root.removeEventListener("touchend", onTouchEnd);
      root.removeEventListener("touchcancel", onTouchEnd);
      root.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("keydown", onKey);
      for (const c of cleanups) c();
    };
    /* `CFG` is built once at module scope, so the engine mounts exactly once. */
  }, []);

  const NAV = [
    { label: "About", ch: 1 },
    { label: "Project", ch: 3 },
    /* the board lives on the LAST stop (`data-ch="6"`) — track the array, not a
     * literal, so re-cutting the beats can never leave the nav one stop short */
    { label: "Contact", ch: STOPS.length - 1 },
  ];

  return (
    <section
      ref={rootRef}
      className={`emul-root relative h-dvh w-full overflow-hidden ${className || ""}`}
      data-intro={introDone ? undefined : ""}
      style={
        {
          backgroundColor: pal.wall,
          color: pal.ink,
          touchAction: "none",
          transition: "background-color .5s, color .5s",
          "--emul-ink": pal.ink,
          "--emul-sub": pal.sub,
          "--emul-nav": pal.nav,
          "--emul-hair": pal.hair,
          "--emul-accent": pal.accent,
          "--emul-card": pal.card,
          "--emul-panel-ink": pal.panelInk,
          "--emul-wall": pal.wall,
        } as React.CSSProperties
      }
      aria-label="Photography portfolio — a wall of prints you pan across"
    >
      {/* the headline ribbon, behind everything */}
      <canvas
        ref={typeRef}
        data-type
        className="pointer-events-none absolute inset-0"
        style={{ zIndex: 1 }}
        aria-hidden
      />

      {/* the wall itself — prints, copy, the contact board */}
      <div ref={wallRef} className="absolute inset-0" style={{ zIndex: 10 }}>
        {/* ── copy ── */}
        {/* `holdat` 0.40 — the intro SITS STILL, then leaves at full rate. With
            the opening beat at 0.80 it used to be gone by 0.246, a third of the
            way in, leaving the column bare while the trio was still migrating.
            Holding first buys the same lateness without touching `rate`: drop
            the rate instead and the copy layer travels slower than the giant
            type behind it, which reverses the depth the whole section reads by.
            It clears the left edge at 0.40 + 0.345/1.4 = 0.646, and the exit
            fade — which runs `from 0.15 → 0.25` — lands at 0.55 → 0.65, so it
            finishes fading exactly as it finishes leaving. */}
        <div data-block data-ch="0" data-holdat="0.4" data-x="0.045" data-y="0.235" data-w="0.3" data-xm="0.05" data-ym="0.2" data-wm="0.8" data-rate="1.4" className="emul-copy absolute left-0 top-0" style={{ zIndex: 25 }}>
          <div data-line className="emul-mask"><div className="emul-eyebrow">Hey — I&apos;m Ines Vahl</div></div>
          <div data-line className="emul-mask"><h2 className="emul-head">A photographer of<br />unhurried light</h2></div>
          <div data-line className="emul-mask"><div className="emul-rule" /></div>
        </div>

        {/* `lead` is NEGATIVE, and that is the point. Every other block opens
            BEFORE its stop; this one has to open AFTER it, because the prints
            land on the stop and the paragraph is not allowed to precede them.
            `lead −0.15` opens the window at `S1 + 0.15` = 0.95, with the trio
            already at rest and the word still parked.

            `span` 0.28 sizes the rise to finish at S2 − 0.25. The last of the
            three lines carries the stagger, so it is home at
            `open + 0.72·span` = 1.15 — a clear quarter-stage of stillness
            before `photographer` starts travelling at 1.40. */}
        <div data-block data-ch="1" data-holdtail="0.45" data-lead="-0.15" data-span="0.28" data-x="0.045" data-y="0.85" data-w="0.32" data-xm="0.05" data-ym="0.8" data-wm="0.86" data-rate="1.4" data-lift="0.18" className="emul-copy absolute left-0 top-0" style={{ zIndex: 25 }}>
          <div data-line className="emul-mask"><div className="emul-index">01 — About</div></div>
          <div data-line className="emul-mask"><p className="emul-body">Fifteen years between a darkroom and a dune. I shoot portraits the way I shoot landscape: wait for the light to say something, then hold very still.</p></div>
        </div>

        <div data-block data-ch="4" data-hold="1" data-x="0.045" data-y="0.78" data-w="0.42" data-xm="0.05" data-ym="0.8" data-wm="0.88" data-rate="1.4" className="emul-copy absolute left-0 top-0" style={{ zIndex: 41 }}>
          <div data-line className="emul-mask"><div className="emul-index">02 — Work</div></div>
          <div data-line className="emul-mask"><h3 className="emul-statement">I like to make pictures that<br />keep developing after<br />you&apos;ve looked away.</h3></div>
        </div>

        {/* ── the prints ── */}
        {CFG.plates.map((p, i) => (
          <div
            key={p.src}
            data-plate
            className="emul-plate absolute left-0 top-0"
            style={{ zIndex: 20 + i * 2, willChange: "transform" }}
          >
            <div data-inner style={{ willChange: "transform" }}>
              <div
                data-card
                className="emul-card"
                style={{
                  filter: p.d > 0.03 ? `blur(${(p.d * 0.75).toFixed(2)}px)` : undefined,
                  opacity: 1 - p.d * 0.07,
                }}
              >
                <div className="emul-win">
                  <img
                    src={`${IMG}${p.src}`}
                    alt={p.cap}
                    draggable={false}
                    /* all eager: a frame must be decoded BEFORE it develops,
                       and lazy-loading can't see through the transform */
                    loading="eager"
                    fetchPriority={p.ch === 0 ? "high" : "low"}
                    decoding="async"
                  />
                  <div data-chin className="emul-chin">
                    <span className="emul-chin-cap">
                      {p.cap} <em>{p.loc}</em>
                    </span>
                    <span className="emul-chin-num">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* ── the contact board ── */}
        <div
          data-block
          data-ch="6"
          data-lead="0.3"
          data-span="0.25"
          data-x="0.2"
          data-y="0.13"
          data-w="0.6"
          data-xm="0.04"
          data-ym="0.14"
          data-wm="0.92"
          data-rate="1.4"
          className="emul-contact absolute left-0 top-0"
          style={{ zIndex: 70, touchAction: "auto" }}
        >
          <div data-line className="emul-mask">
            <div className="emul-panel" style={{ background: pal.accent, color: pal.panelInk }}>
              {sent ? (
                <div className="emul-thanks">
                  <div className="emul-panel-title">Thank you</div>
                  <p className="emul-panel-sub">
                    That&apos;s in the darkroom. I write back within two working days —
                    usually with too many questions about the light.
                  </p>
                  <button
                    type="button"
                    className="emul-send"
                    onClick={() => {
                      setSent(false);
                      setFormError("");
                      setForm({ name: "", email: "", note: "" });
                    }}
                  >
                    Write another
                  </button>
                </div>
              ) : (
                <form onSubmit={submitContact} noValidate>
                  <div className="emul-panel-title">Contact me</div>
                  <p className="emul-panel-sub">
                    Commissions, prints, and the occasional long walk with a camera.
                    Tell me what the light is doing.
                  </p>
                  <label className="emul-field">
                    <span>Name</span>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      autoComplete="name"
                      disabled={sending}
                    />
                  </label>
                  <label className="emul-field">
                    <span>Email</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      autoComplete="email"
                      disabled={sending}
                    />
                  </label>
                  <label className="emul-field">
                    <span>Message</span>
                    <input
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      disabled={sending}
                    />
                  </label>
                  {formError ? (
                    <p className="emul-form-error" role="alert">{formError}</p>
                  ) : null}
                  <button type="submit" className="emul-send" disabled={sending}>
                    {sending ? "Sending" : "Send"}
                  </button>
                </form>
              )}
            </div>
          </div>

          <div data-line className="emul-mask">
            <div className="emul-footer">
              <div><span>Email</span><b>studio@inesvahl.com</b></div>
              <div><span>Telephone</span><b>+34 671 04 22 18</b></div>
              <div><span>Elsewhere</span><b>Instagram · Behance · Are.na</b></div>
              <div className="emul-footer-end"><span>&copy; Ines Vahl, all frames</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* grain + vignette over everything */}
      <canvas
        ref={grainRef}
        className="pointer-events-none absolute"
        style={{
          left: 0, top: 0, zIndex: 90,
          opacity: pal.grain,
          mixBlendMode: "overlay",
          willChange: "transform",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ zIndex: 91, background: pal.vignette }}
        aria-hidden
      />

      {/* ── chrome ── */}
      <div className="emul-chrome" style={{ zIndex: 100 }}>
        <button
          data-chrome
          type="button"
          className="emul-home"
          onClick={() => gotoRef.current(0)}
        >
          Home
        </button>

        <nav data-chrome className="emul-nav">
          {NAV.map((n) => (
            <button
              key={n.label}
              type="button"
              onClick={() => gotoRef.current(n.ch)}
              className={chapter === n.ch ? "is-on" : undefined}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div data-chrome className="emul-scroll">
          <span className="emul-scroll-rule" />
          <span>
            {chapter >= STOPS.length - 1 ? (
              <>End of<br />the wall</>
            ) : (
              <>Scroll to<br />continue</>
            )}
          </span>
        </div>
      </div>

      <style>{`
        .emul-root { -webkit-font-smoothing: antialiased; }
        /* the load fade's starting state — retired via the data-intro attribute.
           The headline is in here for the same reason the prints are: it is
           MEASURED, and until its face has loaded every measurement is wrong.
           The chrome is in here because the entrance now starts on a promise
           rather than at mount: anything GSAP animates from opacity 0 has to be
           hidden by CSS in the meantime, or it paints at full strength and then
           gets yanked back to 0 when the timeline is finally built. */
        .emul-root[data-intro] [data-plate],
        .emul-root[data-intro] [data-type],
        .emul-root[data-intro] [data-chrome] { opacity: 0; }
        .emul-root *, .emul-root *::before { box-sizing: border-box; }

        /* ── copy ── */
        .emul-mask { display: block; overflow: hidden; }
        .emul-mask > * { display: block; will-change: transform, opacity; }
        .emul-eyebrow {
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(10px, 0.85vw, 13px); letter-spacing: .16em;
          text-transform: uppercase; color: var(--emul-sub); margin-bottom: .8em;
        }
        .emul-head {
          font-family: var(--font-playfair), Georgia, serif;
          font-size: clamp(20px, 2.35vw, 40px); line-height: 1.18; font-weight: 400;
          letter-spacing: -0.01em; color: var(--emul-ink); margin: 0;
        }
        .emul-rule { height: 1px; width: 46%; background: var(--emul-ink); opacity: .45; margin-top: 1.4em; }
        .emul-index {
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(9px, 0.72vw, 11px); letter-spacing: .2em;
          text-transform: uppercase; color: var(--emul-accent); margin-bottom: 1em;
        }
        .emul-body {
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(11px, 0.95vw, 15px); line-height: 1.62;
          color: var(--emul-sub); margin: 0; max-width: 34ch;
        }
        .emul-statement {
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(15px, 1.72vw, 30px); line-height: 1.24; font-weight: 600;
          letter-spacing: -0.015em; text-transform: uppercase;
          color: var(--emul-ink); margin: 0;
        }

        /* ── the prints ── */
        .emul-plate { cursor: grab; user-select: none; -webkit-user-select: none; }
        .emul-plate:active { cursor: grabbing; }
        .emul-card {
          background: var(--emul-card);
          display: flex; flex-direction: column;
          transition: box-shadow .28s ease;
        }
        .emul-win {
          position: relative; overflow: hidden; width: 100%;
          background: #0d0b0a; flex: none;
        }
        .emul-win img {
          position: absolute; inset: 0; width: 100%; height: 100%;
          object-fit: cover; display: block; user-select: none;
          -webkit-user-drag: none;
        }
        .emul-chin {
          position: absolute; left: 0; right: 0; bottom: 0; z-index: 2;
          display: flex; align-items: flex-end; justify-content: space-between;
          gap: .9em; padding: 2.4em .9em .75em;
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          letter-spacing: .1em; text-transform: uppercase;
          color: rgba(255,255,255,.94);
          background: linear-gradient(to top, rgba(10,8,6,.74), rgba(10,8,6,0));
          overflow: hidden; white-space: nowrap; pointer-events: none;
          opacity: 0; transition: opacity .32s ease;
        }
        .emul-chin-cap {
          transform: translateY(45%);
          transition: transform .38s cubic-bezier(.16,1,.3,1);
          overflow: hidden; text-overflow: ellipsis;
        }
        .emul-chin-cap em { font-style: normal; opacity: .68; }
        .emul-chin-num { opacity: .7; flex: none; }
        .emul-plate:hover .emul-chin { opacity: 1; }
        .emul-plate:hover .emul-chin-cap { transform: translateY(0); }

        /* ── the contact board ── */
        .emul-panel {
          padding: clamp(24px, 4vw, 62px) clamp(20px, 3vw, 48px);
          min-height: clamp(360px, 66vh, 660px);
          display: flex; flex-direction: column; justify-content: center;
        }
        /* The column grows WITH the panel. Widening the box on its own only
           buys more empty vermillion — the form was capped at 30em, so at any
           panel width past ~580px the fields stopped tracking it and the
           board read as a big flat rectangle with a small form adrift in it. */
        .emul-panel form, .emul-thanks {
          width: 100%; max-width: 36em; margin: 0 auto;
          display: flex; flex-direction: column;
        }
        .emul-panel-title {
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(16px, 1.6vw, 27px); font-weight: 600;
          letter-spacing: .04em; text-transform: uppercase;
        }
        .emul-panel-sub {
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(10px, 0.85vw, 13px); line-height: 1.6;
          opacity: .82; margin: .55em 0 clamp(16px, 2.4vw, 34px); max-width: 40ch;
        }
        .emul-field { display: block; margin-bottom: clamp(12px, 1.7vw, 26px); }
        .emul-field > span {
          display: block; font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(10px, 0.82vw, 13px); font-weight: 600;
          letter-spacing: .05em; margin-bottom: .12em;
        }
        .emul-field input {
          width: 100%; background: transparent; border: 0;
          border-bottom: 1px solid rgba(255,255,255,.45);
          color: inherit; padding: .18em 0 .3em;
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(11px, 0.9vw, 14px); outline: none;
          transition: border-color .25s ease;
        }
        .emul-field input:focus { border-bottom-color: rgba(255,255,255,1); }
        .emul-send {
          margin-top: clamp(8px, 1.2vw, 18px); align-self: flex-start;
          background: #22201D; color: #F7F1E7; border: 0; cursor: pointer;
          padding: .85em 2.6em;
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(11px, 0.85vw, 14px); font-weight: 600; letter-spacing: .04em;
          transition: transform .25s cubic-bezier(.16,1,.3,1), background .25s ease;
        }
        .emul-send:hover { transform: translateY(-2px); background: #100F0D; }
        .emul-send:disabled { cursor: default; opacity: .62; transform: none; }
        .emul-field input:disabled { opacity: .55; cursor: default; }
        .emul-form-error {
          margin: 0 0 clamp(6px, 0.9vw, 12px);
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(10px, 0.82vw, 13px); letter-spacing: .01em;
          color: #2A1410;
        }
        .emul-footer {
          display: flex; flex-wrap: wrap; gap: clamp(16px, 3vw, 54px);
          padding: clamp(14px, 2.2vw, 30px) 0 0;
          font-family: var(--font-geist-sans), system-ui, sans-serif;
        }
        .emul-footer > div { display: flex; flex-direction: column; gap: .35em; }
        .emul-footer span {
          font-size: clamp(9px, 0.7vw, 11px); letter-spacing: .16em;
          text-transform: uppercase; color: var(--emul-sub);
        }
        .emul-footer b {
          font-size: clamp(10px, 0.82vw, 13px); font-weight: 500; color: var(--emul-ink);
        }
        .emul-footer-end { margin-left: auto; justify-content: flex-end; }

        /* ── chrome ── */
        .emul-chrome { position: absolute; inset: 0; pointer-events: none; }
        .emul-chrome > * { pointer-events: auto; }
        /* a print can drift under any corner of the chrome — a halo in the wall's
           own colour keeps the nav legible without laying a scrim over the art */
        .emul-home, .emul-nav button, .emul-scroll {
          text-shadow: 0 0 3px var(--emul-wall), 0 0 5px var(--emul-wall),
                       0 0 9px var(--emul-wall), 0 0 15px var(--emul-wall),
                       0 0 24px var(--emul-wall);
        }
        .emul-home {
          position: absolute; left: clamp(16px, 2.6vw, 44px); top: clamp(16px, 2.4vw, 34px);
          background: none; border: 0; padding: 0; cursor: pointer;
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(10px, 0.74vw, 12px); font-weight: 500;
          letter-spacing: .14em; text-transform: uppercase; color: var(--emul-ink);
          transition: opacity .25s ease;
        }
        .emul-home:hover { opacity: .6; }
        .emul-nav {
          position: absolute; right: clamp(16px, 2.6vw, 44px); top: clamp(16px, 2.4vw, 34px);
          display: flex; flex-direction: column; align-items: flex-end; gap: .5em;
        }
        .emul-nav button {
          background: none; border: 0; cursor: pointer; padding: 0;
          display: flex; align-items: center;
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(10px, 0.74vw, 12px); font-weight: 500; letter-spacing: .14em;
          text-transform: uppercase; color: var(--emul-nav);
          transition: color .62s cubic-bezier(.22,1,.36,1);
        }
        .emul-nav button:hover { color: var(--emul-ink); }
        .emul-nav button.is-on { color: var(--emul-ink); }
        /* the rule is always present so it can grow out of nothing rather than
           snapping in — width and gap ease together on the same curve */
        .emul-nav button::before {
          content: ""; width: 0; height: 1px; margin-right: 0;
          background: var(--emul-accent); opacity: 0; flex: none;
          transition: width .62s cubic-bezier(.22,1,.36,1),
                      margin-right .62s cubic-bezier(.22,1,.36,1),
                      opacity .5s ease;
        }
        .emul-nav button.is-on::before {
          width: 15px; margin-right: .7em; opacity: 1;
        }
        .emul-scroll {
          position: absolute; right: clamp(16px, 2.6vw, 44px); bottom: clamp(16px, 2.4vw, 34px);
          display: flex; align-items: center; gap: .9em;
          font-family: var(--font-geist-sans), system-ui, sans-serif;
          font-size: clamp(9px, 0.68vw, 11px); font-weight: 500; line-height: 1.35;
          letter-spacing: .1em; text-transform: uppercase; color: var(--emul-nav);
        }
        .emul-scroll-rule {
          width: clamp(18px, 2.2vw, 34px); height: 1px;
          background: currentColor; opacity: .5; flex: none;
        }

        /* Matches NARROW in the engine — a tablet is not a small desktop. */
        @media (max-width: 1023px) {
          /* The SITE header owns the top of the frame on a narrow screen: the
             Motion pill sits centred and the avatar top-right, straight over
             this section's own Home and nav. Drop both below it rather than
             letting two navigations argue in the same 60px. */
          .emul-home, .emul-nav { top: clamp(64px, 9vw, 92px); }
          .emul-nav { gap: .5em; }
          .emul-statement { font-size: 17px; }
          .emul-panel { padding: 26px 20px; min-height: 58vh; }
          .emul-footer-end { margin-left: 0; }
        }
        @media (max-width: 560px) {
          .emul-body, .emul-head { max-width: 88vw; }
        }
        @media (prefers-reduced-motion: reduce) {
          .emul-plate { cursor: default; }
        }
      `}</style>
    </section>
  );
}
